import React, { useState, useEffect, useCallback, useRef } from 'react';

const FALLBACK_PRODUCTS = [
  { sku: 'gluck', label: 'Набор глюкометра', kind: 'finished', subtitle: 'готовая продукция' },
  { sku: 'tp50', label: 'ТП50', kind: 'finished', subtitle: 'готовая продукция' },
  { sku: 'tp10', label: 'ТП10', kind: 'raw', subtitle: 'полуфабрикат → склад сырья', output_raw_code: 'PF-TP10' },
  {
    sku: 'scar10',
    label: 'Скарификатор уп.10',
    kind: 'raw',
    subtitle: 'полуфабрикат → склад сырья',
    output_raw_code: 'PF-SCAR10',
  },
];

const SUBTABS = [
  { id: 'stock', label: 'Склад сырья' },
  { id: 'spec', label: 'Спецификация' },
  { id: 'produce', label: 'Выработка' },
  { id: 'writeoffs', label: 'Расходники' },
  { id: 'journals', label: 'Журналы' },
];

function productLabel(sku, products = FALLBACK_PRODUCTS) {
  return products.find((p) => p.sku === sku)?.label
    || products.find((p) => p.sku === sku)?.name
    || sku;
}

/** Группа журнала выработок: pf | tp50 | gluck | other */
function productionGroup(p, products = FALLBACK_PRODUCTS) {
  const sku = String(p?.product_sku || '').trim();
  const skuL = sku.toLowerCase();
  if (skuL === 'tp50') return 'tp50';
  if (skuL === 'gluck') return 'gluck';
  const catalog = products.find((x) => x.sku === sku);
  if (catalog?.kind === 'raw') return 'pf';
  if (/^(pf-|tp10|scar)/i.test(sku)) return 'pf';
  return 'other';
}

function normalizeProducts(list) {
  if (!Array.isArray(list) || list.length === 0) return FALLBACK_PRODUCTS;
  return list.map((p) => ({
    sku: p.sku,
    label: p.label || p.name || p.sku,
    name: p.name || p.label || p.sku,
    kind: p.kind === 'raw' ? 'raw' : 'finished',
    subtitle: p.subtitle
      || (p.kind === 'raw' ? 'полуфабрикат → склад сырья' : 'готовая продукция'),
    output_raw_code: p.output_raw_code || '',
    report_code: p.report_code || '',
  }));
}

function suggestSkuFromName(name) {
  const map = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
    и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
    с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
    ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  };
  const latin = String(name || '').toLowerCase().split('').map((ch) => {
    if (map[ch] != null) return map[ch];
    if (/[a-z0-9]/.test(ch)) return ch;
    return '-';
  }).join('');
  const compact = latin.replace(/-+/g, '').replace(/[^a-z0-9]/g, '').slice(0, 16);
  return compact || `pf${Date.now().toString(36).slice(-5)}`;
}

function isSemiFinishedMaterial(row) {
  const code = String(row?.raw_code || row?.code || '');
  return row?.source === 'production' || code.startsWith('PF-');
}

const FALLBACK_RAW_ZONES = [
  { code: 'pf', label: 'Полуфабрикаты', walk: 1 },
  { code: 'meters', label: 'Приборы / электроника', walk: 2 },
  { code: 'strips', label: 'Полоски / тюбы', walk: 3 },
  { code: 'lancets', label: 'Ланцеты / прокалыватели', walk: 4 },
  { code: 'vials', label: 'Флаконы / крышки', walk: 5 },
  { code: 'cartons', label: 'Коробки / кейсы', walk: 6 },
  { code: 'film', label: 'Плёнка / этикетки / термолента', walk: 7 },
  { code: 'docs', label: 'Инструкции / полиграфия', walk: 8 },
  { code: 'other', label: 'Прочее', walk: 9 },
];

function zoneCodeOf(row, materialsByCode) {
  const key = row?.raw_code || row?.code;
  const m = key ? materialsByCode.get(key) : null;
  if (row?.storage_zone) return row.storage_zone;
  if (m?.storage_zone) return m.storage_zone;
  if (isSemiFinishedMaterial(row) || isSemiFinishedMaterial(m || {})) return 'pf';
  return 'other';
}

function fmtNum(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString('ru-RU');
}

function fmtNorm(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString('ru-RU', { maximumFractionDigits: 4 });
}

/** Имя файла из Content-Disposition — его формирует backend (LOT + дата). */
function filenameFromResponse(res, fallback) {
  const cd = res.headers.get('content-disposition') || '';
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) return decodeURIComponent(star[1]);
  const plain = cd.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
}

function numberInputValue(n) {
  const x = Number(n);
  return Number.isFinite(x) ? String(x) : '';
}

/** ISO → ДД.ММ.ГГГГ (Safari type=date ломает ввод года → 0028). */
function isoToDmy(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

function dmyToIso(dmy) {
  const s = String(dmy || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 2000 || y > 2100) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function formatDmyInput(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function parseExpirationInput(raw) {
  const s = String(raw || '').trim();
  if (!s) return { ok: true, iso: null };
  const iso = dmyToIso(s);
  if (!iso) return { ok: false, iso: null };
  return { ok: true, iso };
}

function groupLabel(g) {
  if (g === 'seal_film') return 'Этикетка';
  return g;
}

function movementLabel(type, qty) {
  if (type === 'expense') return 'Списание';
  if (type === 'receipt') return Number(qty) >= 0 ? 'Приход' : 'Отмена прихода';
  if (type === 'adjustment') return 'Корректировка';
  if (type === 'import') return 'Начальный остаток';
  if (type === 'sync') return 'Синхронизация';
  return type;
}

function UiIcon({ name }) {
  const paths = {
    close: <path d="M6 6l12 12M18 6L6 18" />,
    pdf: (
      <>
        <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V9z" />
        <path d="M14 3v6h6" />
        <path d="M9 13h6M9 17h4" />
      </>
    ),
    pdfBoth: (
      <>
        <path d="M8 8h7a2 2 0 012 2v9a2 2 0 01-2 2H8a2 2 0 01-2-2v-9a2 2 0 012-2z" />
        <path d="M10 8V6a2 2 0 012-2h7a2 2 0 012 2v9a2 2 0 01-2 2h-2" />
      </>
    ),
    gear: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 00.34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0015 19.4a1.7 1.7 0 00-1 .6 1.7 1.7 0 00-.4 1.1V21h-4v-.1A1.7 1.7 0 008.6 19.4a1.7 1.7 0 00-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 004.6 15a1.7 1.7 0 00-.6-1 1.7 1.7 0 00-1.1-.4H3v-4h.1A1.7 1.7 0 004.6 8.6a1.7 1.7 0 00-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 009 4.6a1.7 1.7 0 001-.6 1.7 1.7 0 00.4-1.1V3h4v.1A1.7 1.7 0 0015.4 4a1.7 1.7 0 001.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0019.4 9a1.7 1.7 0 00.6 1 1.7 1.7 0 001.1.4h.1v4h-.1a1.7 1.7 0 00-1.7.6z" />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 10v6M12 7.5h.01" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    refresh: <path d="M20 11a8 8 0 10-2.34 5.66M20 5v6h-6" />,
    trash: <path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />,
  };
  return (
    <svg className="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

/** Сохранить порядок строк; альтернативы одной группы — одним блоком. */
function recipeLineBlocks(lines) {
  const list = lines || [];
  const byGroup = new Map();
  for (const l of list) {
    if (!l.line_group) continue;
    if (!byGroup.has(l.line_group)) byGroup.set(l.line_group, []);
    byGroup.get(l.line_group).push(l);
  }
  const emitted = new Set();
  const blocks = [];
  for (const l of list) {
    if (!l.line_group) {
      blocks.push({ type: 'solo', line: l });
      continue;
    }
    if (emitted.has(l.line_group)) continue;
    emitted.add(l.line_group);
    blocks.push({ type: 'group', group: l.line_group, lines: byGroup.get(l.line_group) });
  }
  return blocks;
}

/** Combobox: поиск сырья по имени / КОД 1С. fetchMaterials(q) — серверный поиск (весь каталог). */
function MaterialPicker({
  materials,
  value,
  onChange,
  placeholder = 'Поиск: имя или КОД…',
  fetchMaterials,
  emptyHint = 'Ничего не найдено. Новую позицию — строка «Новая позиция» → + Добавить.',
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [remoteHits, setRemoteHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const wrapRef = useRef(null);
  const selected = materials.find((m) => m.code === value)
    || (picked?.code === value ? picked : null)
    || remoteHits?.find((m) => m.code === value)
    || null;

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!open || typeof fetchMaterials !== 'function') {
      setRemoteHits(null);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await fetchMaterials(q.trim());
        if (!cancelled) setRemoteHits(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setRemoteHits(null);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, q.trim() ? 180 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [q, open, fetchMaterials]);

  const filtered = (() => {
    if (remoteHits != null) return remoteHits.slice(0, 40);
    const s = q.trim().toLowerCase();
    if (!s) return materials.slice(0, 40);
    return materials.filter((m) => {
      const hay = `${m.name} ${m.code_1c || ''} ${m.code} ${m.sheet_no || ''}`.toLowerCase();
      return hay.includes(s);
    }).slice(0, 40);
  })();

  const pick = (m) => {
    onChange(m.code);
    setPicked(m);
    setOpen(false);
    setQ('');
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (filtered.length === 1) {
      pick(filtered[0]);
      return;
    }
    const s = q.trim().toLowerCase();
    if (!s) return;
    const exact = filtered.find((m) => String(m.code_1c || '').toLowerCase() === s
      || String(m.code || '').toLowerCase() === s
      || String(m.code_1c || '').toLowerCase().endsWith(s));
    if (exact) pick(exact);
  };

  return (
    <div className="mat-picker" ref={wrapRef}>
      <input
        value={open ? q : (selected
          ? `${selected.code_1c ? `${selected.code_1c} · ` : ''}${selected.name}`
          : q)}
        placeholder={placeholder}
        onFocus={() => {
          setOpen(true);
          setQ('');
        }}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open && (
        <div className="mat-picker-list">
          {searching && filtered.length === 0 ? (
            <div className="mat-picker-empty">Поиск…</div>
          ) : filtered.length === 0 ? (
            <div className="mat-picker-empty">{emptyHint}</div>
          ) : filtered.map((m) => (
            <button
              key={m.code}
              type="button"
              className="mat-picker-item"
              onClick={() => pick(m)}
            >
              <span className="mat-picker-code">{m.code_1c || m.code}</span>
              <span>{m.name}</span>
              <span className="mat-picker-qty">{fmtNum(m.qty)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductionPage({
  token,
  logout,
  role,
  apiFetch,
  fmtDateTime,
  formatIsoRu,
  openPdfBlob,
}) {
  const [subtab, setSubtab] = useState('stock');
  const [rawZones, setRawZones] = useState(FALLBACK_RAW_ZONES);
  const [materials, setMaterials] = useState([]);
  const [stockLots, setStockLots] = useState([]);
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [recipes, setRecipes] = useState([]);
  const [productions, setProductions] = useState([]);
  const [movements, setMovements] = useState([]);
  const [audit, setAudit] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [msgSeq, setMsgSeq] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stockQ, setStockQ] = useState('');
  const [invLoading, setInvLoading] = useState(false);
  const [invZeros, setInvZeros] = useState(false);
  const [bomSaving, setBomSaving] = useState(false);
  const [bomEditorOpen, setBomEditorOpen] = useState(false);
  const [pfFormOpen, setPfFormOpen] = useState(false);
  const [pfSaving, setPfSaving] = useState(false);
  const [pfForm, setPfForm] = useState({
    name: '',
    sku: '',
    output_raw_code: '',
    comment: '',
  });
  const [preview, setPreview] = useState(null);
  const [produceStep, setProduceStep] = useState(1); // 1 product → 2 preview → 3 done
  const [lastProductionId, setLastProductionId] = useState(null);
  const [produceBusy, setProduceBusy] = useState(false);
  const [editProd, setEditProd] = useState(null); // { id, ...fields } | null
  const [editProdSaving, setEditProdSaving] = useState(false);
  const [prodJournalFilter, setProdJournalFilter] = useState('all');
  const editProdPanelRef = useRef(null);
  const bomEditorRef = useRef(null);

  const msg = msgText;
  const setMsg = useCallback((text) => {
    setMsgText(text);
    setMsgSeq((n) => n + 1);
  }, []);
  useEffect(() => {
    if (!msgText) return undefined;
    const timer = setTimeout(() => setMsgText(''), 12000);
    return () => clearTimeout(timer);
  }, [msgText, msgSeq]);
  const msgIsWarn = /(ошибк|обязат|недостаточно|не найден|нет сырья|укажите|выберите|нельзя|не удалось|нужен|дубликат)/i
    .test(msgText);

  const [receiptForm, setReceiptForm] = useState({
    raw_code: '',
    qty: '',
    qty_pack: '',
    lot_number: '',
    moved_at: new Date().toISOString().slice(0, 10),
    comment: '',
  });
  const [newMat, setNewMat] = useState({
    name: '', code_1c: '', unit: 'шт', unit_expense: 'шт', factor: '1',
  });
  const [produceForm, setProduceForm] = useState({
    product_sku: 'gluck',
    qty: '',
    recipe_id: '',
    preferred_by_group: {},
    preferred_lots: {},
    lot_number: '',
    expiration_date: '',
    production_end_date: '',
    period_from: '',
    period_to: '',
    archive_qty: '',
    comment: '',
  });
  const [scrapLabForm, setScrapLabForm] = useState({
    product_sku: 'tp50',
    production_id: '',
    lot_number: '',
    scrap_qty: '',
    lab_qty: '',
    raw_code: '',
    moved_at: new Date().toISOString().slice(0, 10),
    comment: '',
  });
  const [suppliesForm, setSuppliesForm] = useState({
    moved_at: new Date().toISOString().slice(0, 10),
    comment: '',
    lines: [{ raw_code: '', qty: '' }],
  });
  const [writeoffs, setWriteoffs] = useState([]);
  const [scrapSuggest, setScrapSuggest] = useState(null);
  const [bomForm, setBomForm] = useState({
    id: null,
    product_sku: 'gluck',
    name: 'Набор глюкометра — 1 шт',
    output_kind: 'finished',
    output_raw_code: '',
    comment: '',
  });
  const [bomLines, setBomLines] = useState([]);
  const [bomAdd, setBomAdd] = useState({
    raw_code: '',
    qty_per_unit: '1',
    line_group: '',
  });
  const [replaceAdd, setReplaceAdd] = useState({ raw_code: '', recipeId: null, group: '' });
  const [replaceSaving, setReplaceSaving] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [editMatForm, setEditMatForm] = useState({});
  const [materialHistory, setMaterialHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTab, setHistoryTab] = useState('journal');
  const [editBomLine, setEditBomLine] = useState(null);
  const [editBomForm, setEditBomForm] = useState({
    qty_per_unit: '', line_group: '', scrap_lab_qty: '', comment: '',
  });

  const canWrite = role === 'warehouse' || role === 'admin';
  const canAdmin = role === 'admin';

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const q = stockQ.trim() ? `?q=${encodeURIComponent(stockQ.trim())}` : '';
      const [m, stock, prodList, r, p, mv, au, wo, zones] = await Promise.all([
        apiFetch(token, '/raw/materials').then((x) => x.json()),
        apiFetch(token, `/raw/stock${q}`).then((x) => x.json()),
        apiFetch(token, '/raw/products').then((x) => x.json()).catch(() => null),
        apiFetch(token, '/raw/recipes').then((x) => x.json()),
        apiFetch(token, '/raw/productions?limit=200').then((x) => x.json()),
        apiFetch(token, '/raw/movements?limit=80').then((x) => x.json()),
        apiFetch(token, '/raw/audit?limit=80').then((x) => x.json()),
        apiFetch(token, '/raw/writeoffs?limit=40').then((x) => x.json()),
        apiFetch(token, '/raw/storage-zones').then((x) => x.json()).catch(() => null),
      ]);
      if (m?.error || !Array.isArray(m)) {
        if (m?.error) setMsg(m.error);
        setMaterials([]);
      } else setMaterials(m);
      setStockLots(Array.isArray(stock) ? stock : []);
      setProducts(normalizeProducts(prodList));
      setRecipes(Array.isArray(r) ? r : []);
      setProductions(Array.isArray(p) ? p : []);
      setMovements(Array.isArray(mv) ? mv : []);
      setAudit(Array.isArray(au) ? au : []);
      setWriteoffs(Array.isArray(wo) ? wo : []);
      if (Array.isArray(zones) && zones.length) {
        setRawZones(zones.map((z, i) => ({
          code: z.code,
          label: z.label || z.code,
          walk: z.walk ?? (i + 1),
        })));
      }
    } finally {
      setLoading(false);
    }
  }, [token, setMsg, apiFetch, stockQ]);

  useEffect(() => { load(); }, [load]);

  const fetchMaterials = useCallback(async (q) => {
    if (!token) return [];
    const params = new URLSearchParams({ limit: '40' });
    if (q) params.set('q', q);
    const res = await apiFetch(token, `/raw/materials?${params}`);
    const data = await res.json().catch(() => []);
    return Array.isArray(data) ? data : [];
  }, [token, apiFetch]);

  const recipe = recipes.find((x) => String(x.id) === String(produceForm.recipe_id))
    || recipes.find((x) => x.product_sku === produceForm.product_sku);

  const groupOptions = (() => {
    const lines = recipe?.lines || [];
    const groups = new Map();
    for (const l of lines) {
      if (!l.line_group) continue;
      if (!groups.has(l.line_group)) groups.set(l.line_group, []);
      groups.get(l.line_group).push(l);
    }
    return [...groups.entries()];
  })();

  const selectedReceiptMat = materials.find((m) => m.code === receiptForm.raw_code);
  const receiptFactor = Number(selectedReceiptMat?.factor) || 1;

  const submitReceipt = async () => {
    if (!receiptForm.raw_code) {
      setMsg('Выберите сырьё из списка (клик по строке или Enter). Новую — «+ Добавить» ниже.');
      return;
    }
    const usePack = receiptForm.qty_pack !== '' && Number(receiptForm.qty_pack) > 0;
    const qty = usePack ? null : Number(receiptForm.qty);
    if (!usePack && !(qty > 0)) {
      setMsg('Укажите кол-во (шт) или pack');
      return;
    }
    setMsg('');
    const body = {
      raw_code: receiptForm.raw_code,
      lot_number: receiptForm.lot_number || undefined,
      moved_at: receiptForm.moved_at || undefined,
      comment: receiptForm.comment?.trim() || undefined,
    };
    if (usePack) body.qty_pack = Number(receiptForm.qty_pack);
    else body.qty = qty;
    const res = await apiFetch(token, '/raw/receipt', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      setMsg(data.error || 'Ошибка прихода');
      return;
    }
    setMsg(`Приход +${data.base_qty}: ${data.material?.name || receiptForm.raw_code}`
      + (receiptForm.lot_number ? ` · LOT ${receiptForm.lot_number}` : ''));
    setReceiptForm({
      ...receiptForm, qty: '', qty_pack: '', lot_number: '', comment: '',
    });
    load();
  };

  const submitNewMaterial = async () => {
    if (!newMat.name.trim()) return;
    const res = await apiFetch(token, '/raw/materials', {
      method: 'POST',
      body: JSON.stringify({
        name: newMat.name.trim(),
        code_1c: newMat.code_1c.trim() || undefined,
        unit: newMat.unit || 'шт',
        unit_expense: newMat.unit_expense || 'шт',
        factor: Number(newMat.factor) || 1,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMsg(data.error || 'Не удалось создать');
      return;
    }
    setMsg(`Добавлено: ${data.name}`);
    setNewMat({ name: '', code_1c: '', unit: 'шт', unit_expense: 'шт', factor: '1' });
    setMaterials((prev) => {
      if (prev.some((m) => m.code === data.code)) {
        return prev.map((m) => (m.code === data.code ? { ...m, ...data } : m));
      }
      return [data, ...prev];
    });
    setReceiptForm({ ...receiptForm, raw_code: data.code });
    load();
  };

  const openEditMaterial = (m, lotRow = null) => {
    setEditMaterial({
      ...m,
      lot_id: lotRow?.lot_id ?? null,
      lot_number: lotRow?.lot_number || '',
    });
    setEditMatForm({
      name: m.name || '',
      code_1c: m.code_1c || '',
      sheet_no: m.sheet_no || '',
      unit: m.unit || 'шт',
      unit_stock: m.unit_stock || m.unit || 'шт',
      unit_expense: m.unit_expense || 'шт',
      factor: String(m.factor ?? 1),
      expense_factor: String(m.expense_factor ?? 1),
      qty: String(m.qty ?? ''),
      storage_zone: m.storage_zone || zoneCodeOf(m, new Map()),
      storage_bin: m.storage_bin || '',
      lot_number: lotRow?.lot_number || '',
      comment: '',
    });
  };

  const openMaterialHistory = async (rawCode) => {
    setHistoryLoading(true);
    setHistoryTab('journal');
    setReplaceAdd({ raw_code: '', recipeId: null, group: '' });
    setMaterialHistory({ material: { code: rawCode }, movements: [], lots: [], totals: {} });
    const res = await apiFetch(
      token,
      `/raw/materials/${encodeURIComponent(rawCode)}/history?limit=300`,
    );
    const data = await res.json().catch(() => ({}));
    setHistoryLoading(false);
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      setMaterialHistory(null);
      setMsg(data.error || 'Не удалось загрузить историю позиции');
      return;
    }
    setMaterialHistory(data);
  };

  /** Группы взаимозаменяемых + соло-строки в спецификациях, где участвует позиция. */
  const materialReplaceGroups = (() => {
    const code = materialHistory?.material?.code;
    if (!code) return [];
    const out = [];
    for (const r of recipes) {
      const lines = r.lines || [];
      const mine = lines.filter((l) => l.raw_code === code);
      for (const m of mine) {
        const group = m.line_group || null;
        out.push({
          recipeId: r.id,
          recipeName: r.name || productLabel(r.product_sku, products),
          group,
          qty_per_unit: m.qty_per_unit,
          siblings: group
            ? lines.filter((l) => l.line_group === group)
            : [m],
          solo: !group,
        });
      }
    }
    return out;
  })();

  const addMaterialReplacement = async (groupInfo) => {
    if (!canAdmin || !groupInfo || replaceSaving) return;
    const newCode = replaceAdd.raw_code;
    if (!newCode) {
      setMsg('Выберите позицию-замену');
      return;
    }
    if (groupInfo.siblings.some((s) => s.raw_code === newCode)) {
      setMsg('Эта позиция уже в группе замен');
      return;
    }
    const recipe = recipes.find((r) => r.id === groupInfo.recipeId);
    if (!recipe) {
      setMsg('Спецификация не найдена');
      return;
    }
    const selfCode = materialHistory?.material?.code;
    const groupKey = groupInfo.group || `alt_${String(selfCode || 'x').replace(/[^a-zA-Z0-9]+/g, '').slice(-12) || 'x'}`;
    const lines = (recipe.lines || []).map((l) => {
      const isSelf = l.raw_code === selfCode && (l.line_group || null) === (groupInfo.group || null);
      return {
        raw_code: l.raw_code,
        qty_per_unit: Number(l.qty_per_unit),
        line_group: isSelf ? groupKey : (l.line_group || null),
      };
    });
    lines.push({
      raw_code: newCode,
      qty_per_unit: Number(groupInfo.qty_per_unit),
      line_group: groupKey,
    });
    setReplaceSaving(true);
    try {
      const res = await apiFetch(token, `/raw/recipes/${recipe.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          product_sku: recipe.product_sku,
          name: recipe.name,
          output_kind: recipe.output_kind || 'finished',
          output_raw_code: recipe.output_raw_code || undefined,
          lines,
          comment: `замена в группе ${groupKey}`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        setMsg(data.error || 'Не удалось добавить замену');
        return;
      }
      if (Array.isArray(data.recipes)) setRecipes(data.recipes);
      else await load();
      setReplaceAdd({ raw_code: '', recipeId: null, group: '' });
      const mat = materials.find((m) => m.code === newCode);
      setMsg(`Замена добавлена: ${mat?.name || newCode} → ${groupLabel(groupKey)}`);
    } catch (e) {
      setMsg(`Сеть/сервер: ${e.message}`);
    } finally {
      setReplaceSaving(false);
    }
  };

  const saveMaterial = async () => {
    if (!editMaterial) return;
    if (!editMatForm.comment.trim() || editMatForm.comment.trim().length < 3) {
      setMsg('Комментарий к правке обязателен (≥3 символа)');
      return;
    }
    const nameRes = await apiFetch(token, `/raw/materials/${encodeURIComponent(editMaterial.code)}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: editMatForm.name,
        code_1c: editMatForm.code_1c,
        sheet_no: editMatForm.sheet_no,
        unit: editMatForm.unit,
        unit_stock: editMatForm.unit_stock,
        unit_expense: editMatForm.unit_expense,
        factor: editMatForm.factor,
        expense_factor: editMatForm.expense_factor,
        storage_zone: editMatForm.storage_zone,
        storage_bin: editMatForm.storage_bin,
        comment: editMatForm.comment.trim(),
      }),
    });
    const nameData = await nameRes.json().catch(() => ({}));
    if (!nameRes.ok) {
      setMsg(nameData.error || 'Ошибка правки карточки');
      return;
    }
    if (
      editMaterial.lot_id
      && String(editMatForm.lot_number || '').trim() !== String(editMaterial.lot_number || '').trim()
    ) {
      const lotRes = await apiFetch(token, `/raw/lots/${editMaterial.lot_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          lot_number: editMatForm.lot_number,
          comment: editMatForm.comment.trim(),
        }),
      });
      const lotData = await lotRes.json().catch(() => ({}));
      if (!lotRes.ok) {
        setMsg(lotData.error || 'Карточка сохранена, но LOT не обновлён');
        load();
        return;
      }
    }
    const newQty = Number(editMatForm.qty);
    if (Number.isFinite(newQty) && newQty !== Number(editMaterial.qty)) {
      const adjRes = await apiFetch(token, `/raw/materials/${encodeURIComponent(editMaterial.code)}/adjust`, {
        method: 'POST',
        body: JSON.stringify({
          qty: newQty,
          comment: editMatForm.comment.trim(),
        }),
      });
      const adjData = await adjRes.json().catch(() => ({}));
      if (!adjRes.ok) {
        setMsg(adjData.error || 'Карточка сохранена, но остаток не обновлён');
        load();
        return;
      }
    }
    setMsg(`Обновлено: ${editMatForm.name}`);
    setEditMaterial(null);
    load();
  };

  const startNewBom = (sku = 'gluck') => {
    const product = products.find((p) => p.sku === sku) || products[0];
    const nextSku = product?.sku || sku;
    setBomForm({
      id: null,
      product_sku: nextSku,
      name: `${productLabel(nextSku, products)} — 1 шт`,
      output_kind: product?.kind === 'raw' ? 'raw' : 'finished',
      output_raw_code: product?.kind === 'raw' ? (product.output_raw_code || '') : '',
      comment: '',
    });
    setBomLines([]);
    setBomAdd({ raw_code: '', qty_per_unit: '1', line_group: '' });
    setPfFormOpen(false);
    setBomEditorOpen(true);
  };

  const loadRecipeIntoBuilder = (r) => {
    const product = products.find((p) => p.sku === r.product_sku);
    setBomForm({
      id: r.id,
      product_sku: r.product_sku,
      name: r.name || `${productLabel(r.product_sku, products)} — 1 шт`,
      output_kind: r.output_kind || product?.kind || 'finished',
      output_raw_code: r.output_raw_code
        || product?.output_raw_code
        || '',
      comment: '',
    });
    setBomLines((r.lines || []).map((l, idx) => ({
      key: `e-${l.id || idx}`,
      raw_code: l.raw_code,
      raw_name: l.raw_name,
      qty_per_unit: numberInputValue(l.qty_per_unit),
      line_group: l.line_group || '',
    })));
    setBomAdd({ raw_code: '', qty_per_unit: '1', line_group: '' });
    setPfFormOpen(false);
    setBomEditorOpen(true);
  };

  const cloneRecipeIntoBuilder = (r) => {
    const product = products.find((p) => p.sku === r.product_sku);
    setBomForm({
      id: null,
      product_sku: r.product_sku,
      name: `${productLabel(r.product_sku, products)} — новый вариант`,
      output_kind: r.output_kind || product?.kind || 'finished',
      output_raw_code: r.output_raw_code
        || product?.output_raw_code
        || '',
      comment: '',
    });
    setBomLines((r.lines || []).map((l, idx) => ({
      key: `clone-${Date.now()}-${l.id || idx}`,
      raw_code: l.raw_code,
      raw_name: l.raw_name,
      qty_per_unit: numberInputValue(l.qty_per_unit),
      line_group: l.line_group || '',
    })));
    setBomAdd({ raw_code: '', qty_per_unit: '1', line_group: '' });
    setPfFormOpen(false);
    setBomEditorOpen(true);
  };

  useEffect(() => {
    if (!bomEditorOpen || !bomEditorRef.current) return undefined;
    const el = bomEditorRef.current;
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 40);
    return () => window.clearTimeout(t);
  }, [bomEditorOpen, bomForm.id]);

  const closeBomEditor = () => {
    setBomEditorOpen(false);
    setBomForm((prev) => ({ ...prev, id: null, comment: '' }));
    setBomLines([]);
  };

  const addBomDraftLine = () => {
    if (!bomAdd.raw_code) {
      setMsg('Выберите сырьё из списка');
      return;
    }
    const group = String(bomAdd.line_group || '').trim();
    const groupPeer = group
      ? bomLines.find((l) => l.line_group === group)
      : null;
    const qty = Number(groupPeer ? groupPeer.qty_per_unit : bomAdd.qty_per_unit);
    if (!(qty > 0)) {
      setMsg('Укажите кол-во на 1 шт продукта (> 0)');
      return;
    }
    const mat = materials.find((m) => m.code === bomAdd.raw_code);
    if (bomLines.some((l) => l.raw_code === bomAdd.raw_code
      && (l.line_group || '') === group)) {
      setMsg('Эта позиция уже в спецификации');
      return;
    }
    setBomLines([
      ...bomLines,
      {
        key: `n-${Date.now()}-${bomAdd.raw_code}`,
        raw_code: bomAdd.raw_code,
        raw_name: mat?.name || bomAdd.raw_code,
        qty_per_unit: String(qty),
        line_group: group,
      },
    ]);
    setBomAdd({ raw_code: '', qty_per_unit: '1', line_group: group });
    setMsg('');
  };

  const saveBomRecipe = async () => {
    if (!canAdmin || bomSaving) return;
    if (!bomForm.comment.trim() || bomForm.comment.trim().length < 3) {
      setMsg('Комментарий к сохранению спецификации обязателен (≥3 символа)');
      return;
    }
    if (bomLines.length === 0) {
      setMsg('Добавьте хотя бы одну позицию сырья');
      return;
    }
    const body = {
      product_sku: bomForm.product_sku,
      name: bomForm.name.trim() || `${productLabel(bomForm.product_sku, products)} — 1 шт`,
      output_kind: bomForm.output_kind,
      output_raw_code: bomForm.output_kind === 'raw' ? bomForm.output_raw_code : undefined,
      lines: bomLines.map((l) => ({
        raw_code: l.raw_code,
        qty_per_unit: Number(l.qty_per_unit),
        line_group: l.line_group || null,
      })),
      comment: bomForm.comment.trim(),
    };
    const url = bomForm.id ? `/raw/recipes/${bomForm.id}` : '/raw/recipes';
    const method = bomForm.id ? 'PUT' : 'POST';
    setBomSaving(true);
    try {
      const res = await apiFetch(token, url, { method, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        setMsg(data.error || `Ошибка сохранения (HTTP ${res.status})`);
        return;
      }
      setMsg(`${bomForm.id ? 'Спецификация обновлена' : 'Спецификация создана'}: ${body.name}`);
      if (Array.isArray(data.recipes)) setRecipes(data.recipes);
      else await load();
      closeBomEditor();
    } catch (e) {
      setMsg(`Сеть/сервер: ${e.message}`);
    } finally {
      setBomSaving(false);
    }
  };

  const deleteBomRecipe = async (r) => {
    if (!canAdmin || !r?.id) return;
    const title = r.name || productLabel(r.product_sku, products);
    if (!window.confirm(`Удалить спецификацию «${title}»?\nВыработки по ней останутся в журнале.`)) return;
    const note = `удал. спецификация #${r.id}`;
    const res = await apiFetch(
      token,
      `/raw/recipes/${r.id}?comment=${encodeURIComponent(note)}`,
      { method: 'DELETE' },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMsg(data.error || 'Ошибка удаления спецификации');
      return;
    }
    setMsg(`Спецификация удалена: ${title}`);
    if (bomForm.id === r.id) closeBomEditor();
    if (Array.isArray(data.recipes)) setRecipes(data.recipes);
    else load();
  };

  const openEditBom = (line) => {
    setEditBomLine(line);
    setEditBomForm({
      qty_per_unit: numberInputValue(line.qty_per_unit),
      line_group: line.line_group || '',
      scrap_lab_qty: line.scrap_lab_qty != null ? numberInputValue(line.scrap_lab_qty) : '',
      comment: '',
    });
  };

  const saveBomLine = async () => {
    if (!editBomLine) return;
    if (!editBomForm.comment.trim() || editBomForm.comment.trim().length < 3) {
      setMsg('Комментарий к правке спецификации обязателен');
      return;
    }
    const res = await apiFetch(token, `/raw/recipes/lines/${editBomLine.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        qty_per_unit: editBomForm.qty_per_unit,
        line_group: editBomForm.line_group,
        scrap_lab_qty: editBomForm.scrap_lab_qty === '' ? null : editBomForm.scrap_lab_qty,
        comment: editBomForm.comment.trim(),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMsg(data.error || 'Ошибка правки');
      return;
    }
    setMsg('Строка спецификации обновлена');
    setEditBomLine(null);
    if (Array.isArray(data.recipes)) setRecipes(data.recipes);
    else load();
  };

  const deleteBomLine = async (line) => {
    if (!canAdmin || !line?.id) return;
    const title = line.raw_name || line.code_1c || line.raw_code || `#${line.id}`;
    if (!window.confirm(`Удалить «${title}» из спецификации?`)) return;
    const note = `удал. ${line.code_1c || line.raw_code || line.id}`;
    const res = await apiFetch(
      token,
      `/raw/recipes/lines/${line.id}?comment=${encodeURIComponent(note)}`,
      { method: 'DELETE' },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMsg(data.error || 'Ошибка удаления');
      return;
    }
    setMsg('Строка удалена из спецификации');
    if (Array.isArray(data.recipes)) setRecipes(data.recipes);
    else load();
  };

  const buildProduceBody = () => {
    const preferred = Object.values(produceForm.preferred_by_group || {}).filter(Boolean);
    const preferredLots = {};
    Object.entries(produceForm.preferred_lots || {}).forEach(([k, v]) => {
      if (v) preferredLots[k] = v;
    });
    const exp = parseExpirationInput(produceForm.expiration_date);
    return {
      product_sku: produceForm.product_sku,
      qty: parseInt(produceForm.qty, 10),
      recipe_id: recipe?.id || undefined,
      comment: produceForm.comment?.trim() || undefined,
      preferred_raw_codes: preferred.length ? preferred : undefined,
      preferred_lots: Object.keys(preferredLots).length ? preferredLots : undefined,
      lot_number: produceForm.lot_number || undefined,
      expiration_date: exp.iso || undefined,
      production_end_date: produceForm.production_end_date || undefined,
      period_from: produceForm.period_from || undefined,
      period_to: produceForm.period_to || undefined,
      archive_qty: produceForm.archive_qty || undefined,
    };
  };

  const ensureProduceExpirationOk = () => {
    const exp = parseExpirationInput(produceForm.expiration_date);
    if (!exp.ok) {
      setMsg('Срок годности: ДД.ММ.ГГГГ');
      return false;
    }
    return true;
  };

  const runPreview = async () => {
    const qty = parseInt(produceForm.qty, 10) || 0;
    if (qty <= 0) {
      setMsg('Укажите количество');
      return false;
    }
    if (!recipe) {
      setMsg(`Нет спецификации для «${productLabel(produceForm.product_sku, products)}»`);
      return false;
    }
    if (recipe.output_kind === 'raw' && !produceForm.lot_number.trim()) {
      setMsg('Для полуфабриката укажите LOT — по нему он поступит на склад сырья');
      return false;
    }
    if (!ensureProduceExpirationOk()) return false;
    setProduceBusy(true);
    try {
      const res = await apiFetch(token, '/raw/produce/preview', {
        method: 'POST',
        body: JSON.stringify(buildProduceBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data.error || 'Ошибка preview');
        setPreview(null);
        return false;
      }
      setPreview(data);
      setMsg('Preview готов — проверьте списание и подтвердите');
      return true;
    } finally {
      setProduceBusy(false);
    }
  };

  const goProduceStep2 = async () => {
    const ok = await runPreview();
    if (ok) setProduceStep(2);
  };

  const submitProduce = async () => {
    const qty = parseInt(produceForm.qty, 10) || 0;
    if (qty <= 0) {
      setMsg('Укажите количество');
      return;
    }
    if (!recipe) {
      setMsg(`Нет спецификации для «${productLabel(produceForm.product_sku, products)}»`);
      return;
    }
    if (!ensureProduceExpirationOk()) return;
    setMsg('');
    setProduceBusy(true);
    try {
      const res = await apiFetch(token, '/raw/produce', {
        method: 'POST',
        body: JSON.stringify(buildProduceBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        setMsg(data.error || 'Ошибка выработки');
        return;
      }
      const lines = (data.consumed || [])
        .map((c) => `${c.raw_name || c.raw_code}: −${c.need}`)
        .join('; ');
      setMsg(`Выработка ${qty} шт ${productLabel(produceForm.product_sku, products)}: ${lines}`);
      const prodId = data.production?.id || null;
      setLastProductionId(prodId);
      setProduceForm({
        ...produceForm,
        qty: '',
        archive_qty: '',
        comment: '',
        lot_number: '',
        preferred_lots: {},
      });
      setPreview(null);
      setProduceStep(3);
      await load();
      if (prodId) {
        await openReportPdf(prodId);
      }
    } finally {
      setProduceBusy(false);
    }
  };

  const resetProduceWizard = () => {
    setProduceStep(1);
    setPreview(null);
    setLastProductionId(null);
    setMsg('');
  };

  const loadScrapSuggest = async (overrides = {}) => {
    const form = { ...scrapLabForm, ...overrides };
    const scrap = parseInt(form.scrap_qty, 10) || 0;
    const lab = parseInt(form.lab_qty, 10) || 0;
    const qty = scrap + lab;
    if (!form.product_sku || qty <= 0) {
      setScrapSuggest(null);
      return;
    }
    const q = new URLSearchParams({
      product_sku: form.product_sku,
      qty: String(qty),
    });
    if (form.production_id) q.set('production_id', String(form.production_id));
    const res = await apiFetch(token, `/raw/writeoffs/suggest?${q}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setScrapSuggest(null);
      setMsg(data.error || 'Не удалось подобрать сырьё для брака/лабы');
      return;
    }
    setScrapSuggest(data);
    const lines = data.lines || [];
    const stillValid = lines.some((l) => l.raw_code === form.raw_code);
    // Сбрасываем залипший raw_code (например 556 после тендера → нарезка).
    if (lines[0]?.raw_code && (!form.raw_code || !stillValid)) {
      setScrapLabForm((prev) => ({ ...prev, ...overrides, raw_code: lines[0].raw_code }));
    }
  };

  const submitScrapLab = async () => {
    const scrapQty = parseInt(scrapLabForm.scrap_qty, 10) || 0;
    const labQty = parseInt(scrapLabForm.lab_qty, 10) || 0;
    const jobs = [
      scrapQty > 0 ? { kind: 'scrap', qty: scrapQty, label: 'Брак' } : null,
      labQty > 0 ? { kind: 'lab', qty: labQty, label: 'Лаба' } : null,
    ].filter(Boolean);
    if (!jobs.length) {
      setMsg('Укажите брак и/или лабораторию (> 0)');
      return;
    }
    if (!scrapLabForm.lot_number.trim() && !scrapLabForm.production_id) {
      setMsg('Укажите LOT ГП или выберите выработку');
      return;
    }

    const suggestLines = scrapSuggest?.lines || [];
    const allowed = new Set([
      ...(scrapSuggest?.allowed_raw_codes || []),
      ...suggestLines.map((l) => l.raw_code),
    ]);
    if (
      scrapLabForm.production_id
      && scrapLabForm.raw_code
      && allowed.size
      && !allowed.has(scrapLabForm.raw_code)
    ) {
      setMsg(
        `Сырьё не из выбранной выработки #${scrapLabForm.production_id}`
        + (scrapSuggest?.recipe?.name ? ` (${scrapSuggest.recipe.name})` : '')
        + '. Выберите позицию из списка или «Авто».',
      );
      return;
    }

    const created = [];
    for (const job of jobs) {
      const matched = suggestLines.find((l) => l.raw_code === scrapLabForm.raw_code);
      const perUnit = matched?.qty_per_unit
        || suggestLines[0]?.qty_per_unit
        || 1;
      // Пустой raw_code → сервер сам возьмёт suggest по production_id.
      const lines = scrapLabForm.raw_code
        ? [{
          raw_code: scrapLabForm.raw_code,
          qty: perUnit * job.qty,
          lot_id: matched?.preferred_lot_id || undefined,
        }]
        : undefined;
      const res = await apiFetch(token, '/raw/writeoffs', {
        method: 'POST',
        body: JSON.stringify({
          kind: job.kind,
          product_sku: scrapLabForm.product_sku,
          production_id: scrapLabForm.production_id || undefined,
          lot_number: scrapLabForm.lot_number || undefined,
          qty: job.qty,
          moved_at: scrapLabForm.moved_at || undefined,
          comment: scrapLabForm.comment?.trim() || undefined,
          lines,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        setMsg(data.error || `Ошибка акта «${job.label}»`
          + (created.length ? ` (уже создано: ${created.map((c) => `#${c.id}`).join(', ')})` : ''));
        load();
        return;
      }
      created.push(data);
    }

    if (created.length && openPdfBlob) {
      const ids = created.map((c) => c.id).filter(Boolean);
      const pdfUrl = ids.length > 1
        ? `/raw/writeoffs/combined/report.pdf?ids=${ids.join(',')}`
        : `/raw/writeoffs/${ids[0]}/report.pdf`;
      const pdfRes = await apiFetch(token, pdfUrl);
      if (pdfRes.ok) {
        const blob = await pdfRes.blob();
        const lot = scrapLabForm.lot_number?.trim() || created[0]?.lot_number || ids[0];
        openPdfBlob(
          blob,
          filenameFromResponse(pdfRes, `Акт на списание по лоту ${lot}.pdf`),
          { autoPrint: false },
        );
      }
    }

    setMsg(created.map((c) => `${c.title || c.kind} #${c.id}`).join(' · '));
    setScrapLabForm({
      ...scrapLabForm,
      scrap_qty: '',
      lab_qty: '',
      comment: '',
      production_id: '',
      raw_code: '',
    });
    setScrapSuggest(null);
    load();
  };

  const submitSupplies = async () => {
    const lines = (suppliesForm.lines || [])
      .filter((l) => l.raw_code && Number(l.qty) > 0)
      .map((l) => ({ raw_code: l.raw_code, qty: Number(l.qty) }));
    if (!lines.length) {
      setMsg('Добавьте хотя бы одну позицию расходников');
      return;
    }
    const res = await apiFetch(token, '/raw/writeoffs', {
      method: 'POST',
      body: JSON.stringify({
        kind: 'supplies',
        moved_at: suppliesForm.moved_at || undefined,
        comment: suppliesForm.comment?.trim() || undefined,
        lines,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      setMsg(data.error || 'Ошибка списания расходников');
      return;
    }
    setMsg(`Акт расходников #${data.id}`);
    setSuppliesForm({
      moved_at: new Date().toISOString().slice(0, 10),
      comment: '',
      lines: [{ raw_code: '', qty: '' }],
    });
    load();
    if (data.id && openPdfBlob) {
      const pdfRes = await apiFetch(token, `/raw/writeoffs/${data.id}/report.pdf`);
      if (pdfRes.ok) {
        const blob = await pdfRes.blob();
        openPdfBlob(
          blob,
          filenameFromResponse(pdfRes, `supplies_${data.id}.pdf`),
          { autoPrint: false },
        );
      }
    }
  };

  const printRawInventory = async () => {
    setInvLoading(true);
    try {
      const qs = invZeros ? '?zeros=1&' : '?';
      const res = await apiFetch(token, `/raw/inventory.pdf${qs}_t=${Date.now()}`);
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMsg(data.error || 'Не удалось сформировать чеклист');
        return;
      }
      const blob = await res.blob();
      openPdfBlob(
        blob,
        filenameFromResponse(res, 'inventarizaciya-syryo.pdf'),
        { autoPrint: false },
      );
      setMsg('Чеклист инвентаризации сырья. Печать → ориентация Landscape.');
    } catch (e) {
      setMsg(`Сеть/сервер: ${e.message}`);
    } finally {
      setInvLoading(false);
    }
  };

  const openWriteoffPdf = async (id) => {
    const res = await apiFetch(token, `/raw/writeoffs/${id}/report.pdf`);
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setMsg(data.error || 'PDF недоступен');
      return;
    }
    const blob = await res.blob();
    openPdfBlob(blob, filenameFromResponse(res, `writeoff_${id}.pdf`), { autoPrint: false });
  };

  const openCombinedWriteoffPdf = async (ids, lotHint) => {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return;
    if (list.length === 1) {
      await openWriteoffPdf(list[0]);
      return;
    }
    const res = await apiFetch(token, `/raw/writeoffs/combined/report.pdf?ids=${list.join(',')}`);
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setMsg(data.error || 'PDF недоступен');
      return;
    }
    const blob = await res.blob();
    const lot = lotHint || list[0];
    openPdfBlob(
      blob,
      filenameFromResponse(res, `Акт на списание по лоту ${lot}.pdf`),
      { autoPrint: false },
    );
  };

  const deleteWriteoffAct = async (w) => {
    if (!canWrite || !w?.id) return;
    const title = w.title || w.kind;
    const lot = w.lot_number ? ` · LOT ${w.lot_number}` : '';
    const qty = w.qty > 0 ? ` · ${fmtNum(w.qty)} шт` : '';
    if (!window.confirm(
      `Удалить «${title}» #${w.id}${lot}${qty}?\n`
      + 'Сырьё вернётся на те же партии склада. Действие необратимо.',
    )) return;
    const note = window.prompt('Причина удаления (обязательно):', 'ошибочно сформирован');
    if (note === null) return;
    const res = await apiFetch(token, `/raw/writeoffs/${w.id}`, {
      method: 'DELETE',
      body: JSON.stringify({ comment: note }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      setMsg(data.error || 'Не удалось удалить акт');
      return;
    }
    const restored = (data.restored || [])
      .map((r) => `${r.raw_code} +${fmtNum(r.qty)}`)
      .join('; ');
    setMsg(`Акт #${w.id} удалён` + (restored ? `: возврат ${restored}` : ''));
    load();
  };

  const deleteProductionRun = async (p) => {
    if (!canWrite || !p?.id) return;
    const sku = productLabel(p.product_sku, products);
    const lot = p.lot_number ? ` · LOT ${p.lot_number}` : '';
    if (!window.confirm(
      `Удалить выработку #${p.id} (${sku}${lot} · ${fmtNum(p.qty)} шт)?\n`
      + 'Сырьё вернётся на те же партии, приход полуфабриката снимется. '
      + 'Склад ГП не меняется. Действие необратимо.',
    )) return;
    const note = window.prompt('Причина удаления (обязательно):', 'дубликат, ошибочно внесена');
    if (note === null) return;
    const res = await apiFetch(token, `/raw/productions/${p.id}`, {
      method: 'DELETE',
      body: JSON.stringify({ comment: note }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      setMsg(data.error || 'Не удалось удалить выработку');
      return;
    }
    if (editProd?.id === p.id) setEditProd(null);
    const restored = (data.restored || [])
      .map((r) => `${r.raw_code} +${fmtNum(r.qty)}`)
      .join('; ');
    setMsg(`Выработка #${p.id} удалена` + (restored ? `: возврат ${restored}` : ''));
    load();
  };

  const revokeProductionLine = async (production, item) => {
    if (!canAdmin || !production?.id || !item?.raw_code) return;
    const title = item.name || item.raw_code;
    const wasQty = Math.abs(Number(item.qty) || 0);
    const answer = window.prompt(
      `Правка «${title}» в выработке #${production.id} (списано ${fmtNum(wasQty)}).\n`
      + 'Введите правильное количество, либо 0 — полное сторно позиции.\n'
      + 'Сырьё вернётся в те же партии, старый расход останется в журнале.',
      '0',
    );
    if (answer === null) return;
    const nextQty = Number(String(answer).replace(',', '.').trim());
    if (!Number.isFinite(nextQty) || nextQty < 0) {
      setMsg('Некорректное количество');
      return;
    }
    const note = window.prompt(
      'Причина правки (обязательно):',
      nextQty > 0 ? 'исправлена норма/единица измерения' : 'позиция исключена из спецификации',
    );
    if (note === null) return;
    const res = await apiFetch(token, `/raw/productions/${production.id}/revoke-line`, {
      method: 'POST',
      body: JSON.stringify({
        raw_code: item.raw_code,
        new_qty: nextQty > 0 ? nextQty : null,
        comment: note,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      setMsg(data.error || 'Ошибка сторно');
      return;
    }
    setMsg(nextQty > 0
      ? `Расход «${title}» в выработке #${production.id}: ${fmtNum(wasQty)} → ${fmtNum(nextQty)}`
      : `Сторно «${title}» по выработке #${production.id} проведено`);
    load();
  };

  const openReportPdf = async (id) => {
    const res = await apiFetch(token, `/raw/productions/${id}/report.pdf`);
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setMsg(data.error || 'Не удалось получить PDF');
      return;
    }
    const blob = await res.blob();
    const filename = filenameFromResponse(res, `production-${id}.pdf`);
    if (openPdfBlob) openPdfBlob(blob, filename, { autoPrint: false });
    else {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
    }
  };

  const openProductionSummaryPdf = async (rawCode) => {
    if (!rawCode) return;
    const res = await apiFetch(
      token,
      `/raw/materials/${encodeURIComponent(rawCode)}/production-summary.pdf?_t=${Date.now()}`,
    );
    if (res.status === 401) { logout(); return; }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setMsg(data.error || 'Не удалось сформировать сводку PDF');
      return;
    }
    const blob = await res.blob();
    const filename = filenameFromResponse(res, 'svodka-syryo.pdf');
    if (openPdfBlob) openPdfBlob(blob, filename, { autoPrint: false });
    else {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
    }
  };

  const toInputDate = (v) => {
    if (!v) return '';
    const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
  };

  const startEditProduction = (p) => {
    const catalog = products.find((x) => x.sku === p.product_sku);
    const isPf = catalog?.kind === 'raw' || Boolean(p.output_raw_code);
    setEditProd({
      id: p.id,
      lot_number: p.lot_number || '',
      qty: String(p.qty ?? ''),
      origQty: Number(p.qty) || 0,
      skip_output: isPf,
      isPf,
      archive_qty: p.archive_qty != null ? String(p.archive_qty) : '',
      expiration_date: isoToDmy(toInputDate(p.expiration_date)),
      production_end_date: toInputDate(p.production_end_date),
      period_from: toInputDate(p.period_from),
      period_to: toInputDate(p.period_to),
      comment: p.comment || '',
      product_sku: p.product_sku,
    });
  };

  useEffect(() => {
    if (!editProd?.id || !editProdPanelRef.current) return undefined;
    const el = editProdPanelRef.current;
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const periodFrom = [...el.querySelectorAll('label')]
        .find((l) => /Период:\s*с/i.test(l.textContent || ''))
        ?.querySelector('input');
      const first = periodFrom || el.querySelector('input');
      if (first && typeof first.focus === 'function') first.focus();
    }, 50);
    return () => window.clearTimeout(t);
  }, [editProd?.id]);

  const saveEditProduction = async () => {
    if (!editProd?.id || editProdSaving) return;
    const exp = parseExpirationInput(editProd.expiration_date);
    if (!exp.ok) {
      setMsg('Срок годности: ДД.ММ.ГГГГ');
      return;
    }
    const nextQty = parseInt(editProd.qty, 10);
    const origQty = Number(editProd.origQty) || 0;
    const qtyChanged = Number.isFinite(nextQty) && nextQty !== origQty;
    if (qtyChanged && !canAdmin) {
      setMsg('Менять упаковано может только admin');
      return;
    }
    if (qtyChanged && nextQty < origQty) {
      setMsg('Уменьшение упаковано не поддержано — удалите выработку или обратитесь к админу');
      return;
    }
    if (qtyChanged && nextQty <= 0) {
      setMsg('Упаковано должно быть > 0');
      return;
    }
    const qtyNote = (editProd.comment || '').trim()
      || (qtyChanged ? `коррекция упаковано ${origQty} → ${nextQty}` : '');
    if (qtyChanged && qtyNote.length < 3) {
      setMsg('Укажите комментарий к правке qty (минимум 3 символа)');
      return;
    }
    setEditProdSaving(true);
    setMsg('');
    try {
      if (qtyChanged) {
        const adj = await apiFetch(token, `/raw/productions/${editProd.id}/adjust-qty`, {
          method: 'POST',
          body: JSON.stringify({
            qty: nextQty,
            comment: qtyNote,
            skip_output: Boolean(editProd.skip_output),
          }),
        });
        const adjData = await adj.json().catch(() => ({}));
        if (adj.status === 401) { logout(); return; }
        if (!adj.ok) {
          setMsg(adjData.error || 'Не удалось изменить упаковано');
          return;
        }
      }
      const res = await apiFetch(token, `/raw/productions/${editProd.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          lot_number: editProd.lot_number,
          expiration_date: exp.iso,
          production_end_date: editProd.production_end_date || null,
          period_from: editProd.period_from || null,
          period_to: editProd.period_to || null,
          archive_qty: editProd.archive_qty === '' ? 0 : parseInt(editProd.archive_qty, 10),
          comment: editProd.comment,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        setMsg(data.error || 'Ошибка сохранения реквизитов');
        return;
      }
      const bits = [];
      if (qtyChanged) {
        bits.push(`упаковано ${origQty} → ${nextQty}`);
        if (editProd.skip_output) bits.push('ПФ на складе не трогали');
        else bits.push('ПФ доприходован');
      }
      if (data.archive_synced) {
        bits.push(`дописан архив (${(data.archive_deducted || []).length} дв.)`);
      }
      setMsg(bits.length
        ? `Выработка #${editProd.id}: ${bits.join(', ')}`
        : `Реквизиты выработки #${editProd.id} обновлены`);
      setEditProd(null);
      load();
    } finally {
      setEditProdSaving(false);
    }
  };

  const createPfProduct = async () => {
    if (!canAdmin || pfSaving) return;
    const name = pfForm.name.trim();
    if (name.length < 2) {
      setMsg('Укажите название полуфабриката');
      return;
    }
    const sku = (pfForm.sku.trim() || suggestSkuFromName(name)).toLowerCase();
    const output_raw_code = (pfForm.output_raw_code.trim() || `PF-${sku.toUpperCase()}`).toUpperCase();
    setPfSaving(true);
    try {
      const res = await apiFetch(token, '/raw/products', {
        method: 'POST',
        body: JSON.stringify({
          name,
          sku,
          kind: 'raw',
          output_raw_code,
          comment: pfForm.comment.trim() || `добавлен ПФ ${name}`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { logout(); return; }
      if (!res.ok) {
        setMsg(data.error || `Ошибка создания ПФ (HTTP ${res.status})`);
        return;
      }
      const next = normalizeProducts(data.products);
      setProducts(next);
      const created = next.find((p) => p.sku === (data.product?.sku || sku));
      setMsg(`Полуфабрикат добавлен: ${created?.label || name}`);
      setPfFormOpen(false);
      setPfForm({ name: '', sku: '', output_raw_code: '', comment: '' });
      if (created) {
        setBomForm({
          id: null,
          product_sku: created.sku,
          name: `${created.label} — 1 шт`,
          output_kind: 'raw',
          output_raw_code: created.output_raw_code || output_raw_code,
          comment: '',
        });
        setBomLines([]);
        setBomEditorOpen(true);
      }
      await load();
    } catch (e) {
      setMsg(`Сеть/сервер: ${e.message}`);
    } finally {
      setPfSaving(false);
    }
  };

  const produceSkus = [
    ...products.filter((p) => recipes.some((r) => r.product_sku === p.sku)),
    ...products.filter((p) => !recipes.some((r) => r.product_sku === p.sku)),
  ];
  const finishedProduceSkus = produceSkus.filter((p) => p.kind !== 'raw');
  const semiProduceSkus = produceSkus.filter((p) => p.kind === 'raw');
  const finishedRecipes = recipes.filter((r) => r.output_kind !== 'raw');
  const semiRecipes = recipes.filter((r) => r.output_kind === 'raw');
  const prodJournalCounts = {
    all: productions.length,
    pf: productions.filter((p) => productionGroup(p, products) === 'pf').length,
    tp50: productions.filter((p) => productionGroup(p, products) === 'tp50').length,
    gluck: productions.filter((p) => productionGroup(p, products) === 'gluck').length,
  };
  const filteredProductions = prodJournalFilter === 'all'
    ? productions
    : productions.filter((p) => productionGroup(p, products) === prodJournalFilter);
  const editingRecipeId = bomEditorOpen && bomForm.id ? bomForm.id : null;
  const creatingBom = bomEditorOpen && !bomForm.id;
  const selectedBomProduct = products.find((p) => p.sku === bomForm.product_sku);

  const materialsByCode = new Map(materials.map((m) => [m.code, m]));
  const matchesStockQ = (m) => {
    const s = stockQ.trim().toLowerCase();
    if (!s) return true;
    const hay = `${m.name || ''} ${m.code_1c || ''} ${m.code || ''} ${m.sheet_no || ''} ${m.sheet_lot || ''} ${m.storage_zone || ''} ${m.storage_bin || ''}`.toLowerCase();
    return hay.includes(s);
  };
  const codesShown = new Set(stockLots.map((r) => r.raw_code));
  const emptyStockRows = materials
    .filter((m) => !codesShown.has(m.code) && matchesStockQ(m))
    .map((m) => ({
      lot_id: `empty-${m.code}`,
      raw_code: m.code,
      code_1c: m.code_1c,
      name: m.name,
      lot_number: null,
      qty: Number(m.qty) || 0,
      unit_expense: m.unit_expense || m.unit,
      unit_stock: m.unit_stock || m.unit,
      factor: m.factor,
      pack_qty: Number(m.factor) > 0
        ? Math.round(((Number(m.qty) || 0) / Number(m.factor)) * 10000) / 10000
        : null,
      source: m.source,
      storage_zone: m.storage_zone,
      storage_bin: m.storage_bin,
      empty: true,
    }));
  const allStockRows = [...stockLots, ...emptyStockRows];
  const stockByZone = rawZones.map((z) => ({
    ...z,
    rows: allStockRows.filter((row) => zoneCodeOf(row, materialsByCode) === z.code),
  })).filter((z) => z.rows.length > 0);

  const renderBomEditor = () => (
    <div className="bom-builder" ref={bomEditorRef}>
      <div className="bom-editor-head">
        <div>
          <b>{bomForm.id ? `Редактирование · #${bomForm.id}` : 'Новая спецификация'}</b>
          <span>
            {bomForm.id
              ? `Меняется выбранная спецификация «${bomForm.name}» — сохранится по её id`
              : 'Нормы расхода на 1 единицу выпуска'}
          </span>
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={closeBomEditor}
          aria-label="Закрыть"
          title="Закрыть"
        >
          <UiIcon name="close" />
        </button>
      </div>

      {!bomForm.id && (
        <>
          <div className="bom-product-picker" role="group" aria-label="Продукт">
            {products.filter((p) => p.kind !== 'raw').map((p) => (
              <button
                key={p.sku}
                type="button"
                className={bomForm.product_sku === p.sku ? 'active' : ''}
                onClick={() => setBomForm({
                  ...bomForm,
                  product_sku: p.sku,
                  name: `${p.label} — 1 шт`,
                  output_kind: 'finished',
                  output_raw_code: '',
                })}
              >
                <b>{p.label}</b>
                <span>{p.subtitle || 'готовая продукция'}</span>
              </button>
            ))}
            {products.filter((p) => p.kind === 'raw').map((p) => (
              <button
                key={p.sku}
                type="button"
                className={`bom-pf-pick${bomForm.product_sku === p.sku ? ' active' : ''}`}
                onClick={() => setBomForm({
                  ...bomForm,
                  product_sku: p.sku,
                  name: `${p.label} — 1 шт`,
                  output_kind: 'raw',
                  output_raw_code: p.output_raw_code || '',
                })}
              >
                <b>{p.label}</b>
                <span>{p.subtitle || 'полуфабрикат → склад сырья'}</span>
              </button>
            ))}
            <button
              type="button"
              className={`bom-pf-add${pfFormOpen ? ' active' : ''}`}
              onClick={() => {
                setPfFormOpen((open) => !open);
                if (!pfForm.name) {
                  setPfForm({
                    name: '',
                    sku: '',
                    output_raw_code: '',
                    comment: '',
                  });
                }
              }}
            >
              <b>+ Полуфабрикат</b>
              <span>новый тип ПФ на склад сырья</span>
            </button>
          </div>

          {pfFormOpen && (
            <div className="bom-pf-create">
              <div className="stock-inline-form">
                <label className="grow">Название ПФ *
                  <input
                    value={pfForm.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      const autoSku = suggestSkuFromName(name);
                      setPfForm((prev) => ({
                        ...prev,
                        name,
                        sku: prev.sku || autoSku,
                        output_raw_code: prev.output_raw_code
                          || `PF-${(prev.sku || autoSku).toUpperCase()}`,
                      }));
                    }}
                    placeholder="Скарификатор в уп. по 10 шт"
                  />
                </label>
                <label>SKU
                  <input
                    value={pfForm.sku}
                    onChange={(e) => {
                      const sku = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                      setPfForm({
                        ...pfForm,
                        sku,
                        output_raw_code: pfForm.output_raw_code.startsWith('PF-')
                          ? `PF-${sku.toUpperCase() || 'NEW'}`
                          : pfForm.output_raw_code,
                      });
                    }}
                    placeholder="scar10"
                  />
                </label>
                <label>Код склада
                  <input
                    value={pfForm.output_raw_code}
                    onChange={(e) => setPfForm({
                      ...pfForm,
                      output_raw_code: e.target.value.toUpperCase(),
                    })}
                    placeholder="PF-SCAR10"
                  />
                </label>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={createPfProduct}
                  disabled={pfSaving}
                >
                  {pfSaving ? 'Добавляю…' : 'Добавить ПФ'}
                </button>
              </div>
              <p className="hint-small">
                Создаст позицию на складе сырья (source=production) и пункт в picker —
                дальше собираете спецификацию как у ТП10.
              </p>
            </div>
          )}
        </>
      )}

      <div className="stock-inline-form bom-meta-row">
        <label className="grow">Название
          <input
            value={bomForm.name}
            onChange={(e) => setBomForm({ ...bomForm, name: e.target.value })}
          />
        </label>
      </div>

      {bomForm.output_kind === 'raw' && (
        <div className="bom-output-note">
          Результат выработки оприходуется в папку «Полуфабрикаты» как
          <b>
            {' '}
            {selectedBomProduct?.label || productLabel(bomForm.product_sku, products)}
            {' · '}
            {bomForm.output_raw_code || selectedBomProduct?.output_raw_code || 'PF-…'}
          </b>
          {' '}с указанным LOT — затем доступен в спецификации готовой продукции.
        </div>
      )}

      <div className="stock-inline-form bom-add-row">
        <label className="grow">Сырьё
          <MaterialPicker
            materials={materials}
            value={bomAdd.raw_code}
            onChange={(code) => setBomAdd({ ...bomAdd, raw_code: code })}
            fetchMaterials={fetchMaterials}
          />
        </label>
        <label>На 1 шт
          <input
            type="number"
            min="0.0001"
            step="any"
            value={bomAdd.qty_per_unit}
            onChange={(e) => setBomAdd({ ...bomAdd, qty_per_unit: e.target.value })}
            disabled={Boolean(bomAdd.line_group
              && bomLines.some((l) => l.line_group === bomAdd.line_group.trim()))}
          />
        </label>
        <label>Группа замен
          <input
            list="bom-line-groups"
            value={bomAdd.line_group}
            onChange={(e) => setBomAdd({ ...bomAdd, line_group: e.target.value })}
            placeholder="пусто = обязат."
            title="Одинаковый ключ = взаимозаменяемые (водопад по остатку)"
          />
          <datalist id="bom-line-groups">
            {[...new Set(bomLines.map((l) => l.line_group).filter(Boolean))].map((g) => (
              <option key={g} value={g}>{groupLabel(g)}</option>
            ))}
            <option value="seal_film">Этикетка</option>
          </datalist>
        </label>
        <button type="button" className="btn-header" onClick={addBomDraftLine}>
          + В спецификацию
        </button>
      </div>

      {bomLines.length > 0 && (
        <table className="stats-table bom-draft-table">
          <thead>
            <tr>
              <th>Сырьё</th><th>На 1 шт</th>
              <th>Группа</th><th></th>
            </tr>
          </thead>
          <tbody>
            {recipeLineBlocks(bomLines).map((block) => {
              if (block.type === 'solo') {
                const l = block.line;
                return (
                  <tr key={l.key}>
                    <td>{l.raw_name || l.raw_code}</td>
                    <td>
                      <input
                        className="bom-qty-input"
                        type="number"
                        min="0.0001"
                        step="any"
                        value={l.qty_per_unit}
                        onChange={(e) => setBomLines(bomLines.map((x) => (
                          x.key === l.key ? { ...x, qty_per_unit: e.target.value } : x
                        )))}
                      />
                    </td>
                    <td className="cell-muted">обязат.</td>
                    <td>
                      <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        onClick={() => setBomLines(bomLines.filter((x) => x.key !== l.key))}
                        aria-label={`Убрать ${l.raw_name || l.raw_code}`}
                        title="Убрать"
                      >
                        <UiIcon name="trash" />
                      </button>
                    </td>
                  </tr>
                );
              }
              const colSpan = 4;
              return (
                <React.Fragment key={`g-${block.group}`}>
                  <tr className="bom-group-header">
                    <td colSpan={colSpan}>
                      {groupLabel(block.group)}
                      {' · '}
                      расход {fmtNorm(block.lines[0]?.qty_per_unit)} шт на изделие
                      {' · '}
                      {block.lines.length} взаимозаменяемые позиции
                    </td>
                  </tr>
                  {block.lines.map((l) => (
                    <tr key={l.key} className="bom-group-alt">
                      <td>{l.raw_name || l.raw_code}</td>
                      <td>
                        <input
                          className="bom-qty-input"
                          type="number"
                          min="0.0001"
                          step="any"
                          value={l.qty_per_unit}
                          onChange={(e) => setBomLines(bomLines.map((x) => (
                            x.key === l.key ? { ...x, qty_per_unit: e.target.value } : x
                          )))}
                        />
                      </td>
                      <td className="cell-muted">замена</td>
                      <td>
                        <button
                          type="button"
                          className="icon-btn icon-btn-danger"
                          onClick={() => setBomLines(bomLines.filter((x) => x.key !== l.key))}
                          aria-label={`Убрать ${l.raw_name || l.raw_code}`}
                          title="Убрать"
                        >
                          <UiIcon name="trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bom-group-spacer"><td colSpan={colSpan} /></tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="stock-inline-form" style={{ marginTop: 10 }}>
        <label className="grow">Комментарий *
          <input
            value={bomForm.comment}
            onChange={(e) => setBomForm({ ...bomForm, comment: e.target.value })}
            placeholder="создал / правка состава"
          />
        </label>
        <button
          type="button"
          className="btn-primary"
          onClick={saveBomRecipe}
          disabled={bomSaving}
        >
          {bomSaving ? 'Сохраняю…' : (bomForm.id ? 'Сохранить изменения' : 'Создать')}
        </button>
      </div>
    </div>
  );

  const renderRecipeCard = (r) => {
    const isEditing = editingRecipeId === r.id;
    return (
      <div
        key={r.id}
        className={`tender-lot-block bom-recipe-card${isEditing ? ' bom-recipe-editing' : ''}`}
        style={{ marginTop: 14, marginBottom: 4 }}
      >
        <div className="tender-lot-summary">
          <span className="tender-lot-code">{productLabel(r.product_sku, products)}</span>
          <span>{r.name}</span>
          <span className="cell-muted">#{r.id} · {r.product_code}</span>
          <span className={`bom-output-badge ${r.output_kind === 'raw' ? 'raw' : ''}`}>
            {r.output_kind === 'raw' ? 'полуфабрикат → сырьё' : 'готовая продукция'}
          </span>
          {canAdmin && (
            <div className="bom-summary-actions">
              <button type="button" className="btn-variant" onClick={() => cloneRecipeIntoBuilder(r)}>
                <UiIcon name="plus" />
                Новый вариант
              </button>
              <button
                type="button"
                className={`icon-btn${isEditing ? ' icon-btn-active' : ''}`}
                onClick={() => (isEditing ? closeBomEditor() : loadRecipeIntoBuilder(r))}
                aria-label={`Редактировать ${r.name}`}
                title={isEditing ? 'Закрыть редактор' : 'Редактировать эту спецификацию'}
              >
                <UiIcon name="gear" />
              </button>
              <button
                type="button"
                className="icon-btn icon-btn-danger"
                onClick={() => deleteBomRecipe(r)}
                aria-label={`Удалить ${r.name}`}
                title="Удалить спецификацию"
              >
                <UiIcon name="trash" />
              </button>
            </div>
          )}
        </div>

        {isEditing ? renderBomEditor() : (
          <table className="stats-table tender-lots-table">
            <thead>
              <tr>
                <th>КОД</th><th>Сырьё</th><th>На 1 шт</th><th>Остаток</th>
                {canAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {recipeLineBlocks(r.lines || []).map((block) => {
                if (block.type === 'solo') {
                  const l = block.line;
                  return (
                    <tr key={l.id}>
                      <td className="cell-muted">{l.code_1c || '—'}</td>
                      <td>{l.raw_name || l.raw_code}</td>
                      <td><b>{fmtNorm(l.qty_per_unit)}</b></td>
                      <td>{fmtNum(l.raw_qty)}</td>
                      {canAdmin && (
                        <td className="table-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => openEditBom(l)}
                            aria-label={`Изменить ${l.raw_name || l.raw_code}`}
                            title="Изменить"
                          >
                            <UiIcon name="gear" />
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn-danger"
                            onClick={() => deleteBomLine(l)}
                            aria-label={`Удалить ${l.raw_name || l.raw_code}`}
                            title="Удалить"
                          >
                            <UiIcon name="trash" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                }
                const colSpan = canAdmin ? 5 : 4;
                const groupHave = block.lines.reduce((s, x) => s + (Number(x.raw_qty) || 0), 0);
                return (
                  <React.Fragment key={`rg-${r.id}-${block.group}`}>
                    <tr className="bom-group-header">
                      <td colSpan={colSpan}>
                        {groupLabel(block.group)}
                        {' · '}
                        расход {fmtNorm(block.lines[0]?.qty_per_unit)} шт на изделие
                        {' · '}
                        доступно {fmtNum(groupHave)}
                      </td>
                    </tr>
                    {block.lines.map((l) => (
                      <tr key={l.id} className="bom-group-alt">
                        <td className="cell-muted">{l.code_1c || '—'}</td>
                        <td>{l.raw_name || l.raw_code}</td>
                        <td><span className="bom-alt-mark">или</span></td>
                        <td>{fmtNum(l.raw_qty)}</td>
                        {canAdmin && (
                          <td className="table-actions">
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => openEditBom(l)}
                              aria-label={`Изменить ${l.raw_name || l.raw_code}`}
                              title="Изменить"
                            >
                              <UiIcon name="gear" />
                            </button>
                            <button
                              type="button"
                              className="icon-btn icon-btn-danger"
                              onClick={() => deleteBomLine(l)}
                              aria-label={`Удалить ${l.raw_name || l.raw_code}`}
                              title="Удалить"
                            >
                              <UiIcon name="trash" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr className="bom-group-spacer"><td colSpan={colSpan} /></tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderRecipeFolder = (title, list, emptyHint) => (
    <div className="bom-folder" key={title}>
      <div className="bom-folder-head">
        <h4>{title}</h4>
        <span className="cell-muted">{list.length}</span>
      </div>
      {list.length === 0 ? (
        <p className="hint-small">{emptyHint}</p>
      ) : list.map(renderRecipeCard)}
    </div>
  );

  const renderStockTable = (rows, emptyText) => {
    if (rows.length === 0) {
      return <p className="hint-small">{emptyText}</p>;
    }
    return (
      <div className="stock-journal-scroll">
        <table className="stats-table">
          <thead>
            <tr>
              <th>КОД</th><th>Наименование</th><th>LOT</th>
              <th>Ед</th><th>Остаток</th><th>≈ pack</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.raw_code}-${row.lot_id}`}
                className={`raw-stock-row${Number(row.qty) === 0 ? ' raw-stock-row-zero' : ''}`}
                onClick={(e) => {
                  if (e.target.closest('button, a, input, select, textarea')) return;
                  openMaterialHistory(row.raw_code);
                }}
              >
                <td className="cell-muted cell-nowrap">{row.code_1c || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="raw-material-link"
                    onClick={() => openMaterialHistory(row.raw_code)}
                    title="Открыть историю движений"
                  >
                    {row.name}
                  </button>
                  {row.storage_bin && (
                    <div className="cell-muted" style={{ fontSize: 11 }}>{row.storage_bin}</div>
                  )}
                </td>
                <td className="cell-nowrap">{row.lot_number || (row.empty ? 'нет партий' : '—')}</td>
                <td className="cell-muted">{row.unit_expense || row.unit || 'шт'}</td>
                <td className={Number(row.qty) === 0 ? 'cell-muted' : ''}>
                  <b>{fmtNum(row.qty)}</b>
                </td>
                <td className="cell-muted">
                  {row.pack_qty != null && Number(row.factor) > 1
                    ? `${fmtNum(row.pack_qty)} ${row.unit_stock || row.unit}`
                    : '—'}
                </td>
                <td>
                  <div className="table-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => openMaterialHistory(row.raw_code)}
                      aria-label={`История ${row.name}`}
                      title="История движений"
                    >
                      <UiIcon name="info" />
                    </button>
                    {canWrite && (
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => {
                          const mat = materials.find((m) => m.code === row.raw_code);
                          if (mat) openEditMaterial(mat, row.empty ? null : row);
                        }}
                        aria-label={`Изменить ${row.name}`}
                        title="Изменить"
                      >
                        <UiIcon name="gear" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="raw-page">
      <div className="production-toolbar">
        <div className="accounting-actions">
          <button
            type="button"
            className="icon-btn"
            onClick={load}
            disabled={loading}
            aria-label="Обновить данные"
            title="Обновить данные"
          >
            <UiIcon name="refresh" />
          </button>
        </div>
      </div>

      <div className="prod-subtabs">
        {SUBTABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={subtab === t.id ? 'active' : ''}
            onClick={() => setSubtab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {msg && (
        <div className={`raw-toast${msgIsWarn ? ' raw-toast-warn' : ''}`} role="status">
          <span>{msg}</span>
          <button type="button" className="raw-toast-close" onClick={() => setMsgText('')} aria-label="Закрыть">×</button>
        </div>
      )}

      {/* —— Склад сырья —— */}
      {subtab === 'stock' && (
        <>
          {canWrite && (
            <section className="stock-card">
              <div className="stock-card-head"><h3>Приход сырья</h3></div>
              <div className="stock-inline-form">
                <label className="grow">Сырьё
                  <MaterialPicker
                    materials={materials}
                    value={receiptForm.raw_code}
                    onChange={(code) => setReceiptForm({ ...receiptForm, raw_code: code })}
                    fetchMaterials={fetchMaterials}
                    emptyHint={canWrite
                      ? 'Ничего не найдено. Новую позицию — строка ниже → + Добавить.'
                      : 'Ничего не найдено'}
                  />
                </label>
                <label>LOT
                  <input
                    value={receiptForm.lot_number}
                    onChange={(e) => setReceiptForm({ ...receiptForm, lot_number: e.target.value })}
                    placeholder="опц."
                  />
                </label>
                <label>Pack
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={receiptForm.qty_pack}
                    onChange={(e) => setReceiptForm({ ...receiptForm, qty_pack: e.target.value, qty: '' })}
                    placeholder={selectedReceiptMat ? `×${receiptFactor}` : ''}
                  />
                </label>
                <label>Шт (base)
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={receiptForm.qty}
                    onChange={(e) => setReceiptForm({ ...receiptForm, qty: e.target.value, qty_pack: '' })}
                  />
                </label>
                <label>Дата
                  <input
                    type="date"
                    value={receiptForm.moved_at}
                    onChange={(e) => setReceiptForm({ ...receiptForm, moved_at: e.target.value })}
                  />
                </label>
                <label>Коммент
                  <input
                    value={receiptForm.comment}
                    onChange={(e) => setReceiptForm({ ...receiptForm, comment: e.target.value })}
                    placeholder="накладная"
                  />
                </label>
                <button type="button" className="btn-primary" onClick={submitReceipt}>Оприходовать</button>
              </div>
              {selectedReceiptMat && receiptForm.qty_pack && (
                <p className="hint-small">
                  = {(Number(receiptForm.qty_pack) * receiptFactor).toLocaleString('ru-RU')} шт
                  {' '}(factor {receiptFactor} {selectedReceiptMat.unit_stock || selectedReceiptMat.unit})
                </p>
              )}
              {canWrite && (
                <div className="stock-inline-form" style={{ marginTop: 8 }}>
                  <label className="grow">Новая позиция
                    <input
                      value={newMat.name}
                      onChange={(e) => setNewMat({ ...newMat, name: e.target.value })}
                      placeholder="Наименование"
                    />
                  </label>
                  <label>КОД 1С
                    <input
                      value={newMat.code_1c}
                      onChange={(e) => setNewMat({ ...newMat, code_1c: e.target.value })}
                    />
                  </label>
                  <label>Ед прихода
                    <input
                      value={newMat.unit}
                      onChange={(e) => setNewMat({ ...newMat, unit: e.target.value })}
                    />
                  </label>
                  <label>Factor
                    <input
                      type="number"
                      min="0.0001"
                      step="any"
                      value={newMat.factor}
                      onChange={(e) => setNewMat({ ...newMat, factor: e.target.value })}
                    />
                  </label>
                  <button type="button" className="btn-header" onClick={submitNewMaterial}>+ Добавить</button>
                </div>
              )}
            </section>
          )}

          <section className="stock-card">
            <div className="stock-card-head">
              <h3>Склад сырья</h3>
              <div className="stock-card-head-right">
                <input
                  className="prod-search"
                  value={stockQ}
                  onChange={(e) => setStockQ(e.target.value)}
                  placeholder="Поиск КОД / имя / LOT / зона"
                />
                <label className="stock-inline-label" style={{ whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={invZeros}
                    onChange={(e) => setInvZeros(e.target.checked)}
                  />
                  {' '}нулевые в PDF
                </label>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={invLoading}
                  onClick={printRawInventory}
                >
                  {invLoading ? 'PDF…' : 'Инвентаризация сырья'}
                </button>
              </div>
            </div>
            <p className="hint-small raw-zone-map">
              Карта обхода:
              {' '}
              {rawZones.map((z, i) => (
                <span key={z.code}>
                  {i ? ' → ' : ''}
                  <b>{z.walk}. {z.label}</b>
                </span>
              ))}
              . Зону/ячейку можно сменить в карточке позиции.
            </p>

            {stockByZone.length === 0 ? (
              <p className="hint-small">Пусто. Сделайте импорт или приход.</p>
            ) : stockByZone.map((z) => (
              <div
                className={`bom-folder${z.code === 'pf' ? ' bom-folder-pf' : ''}`}
                key={z.code}
              >
                <div className="bom-folder-head">
                  <h4>{z.walk}. {z.label}</h4>
                  <span className="cell-muted">{z.rows.length}</span>
                </div>
                {z.code === 'pf' && (
                  <p className="hint-small" style={{ marginBottom: 8 }}>
                    ПФ после выработки (ТП10, скарификатор уп.10 и др.) — затем в BOM готовой продукции.
                  </p>
                )}
                {renderStockTable(z.rows, 'В этой зоне пусто.')}
              </div>
            ))}
          </section>
        </>
      )}

      {/* —— Спецификация —— */}
      {subtab === 'spec' && (
        <section className="stock-card">
          <div className="stock-card-head">
            <div>
              <h3>Спецификации</h3>
              <p className="hint-small">Состав одной единицы продукта или полуфабриката</p>
            </div>
            {canAdmin && (
              <div className="stock-card-head-right">
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => startNewBom(bomForm.product_sku)}
                  aria-label="Новая спецификация"
                  title="Новая спецификация"
                >
                  <UiIcon name="plus" />
                </button>
              </div>
            )}
          </div>

          {creatingBom && canAdmin && renderBomEditor()}

          {recipes.length === 0 && !creatingBom ? (
            <p className="hint-small" style={{ marginTop: 12 }}>Сохранённых спецификаций пока нет.</p>
          ) : (
            <>
              {renderRecipeFolder(
                'Готовая продукция',
                finishedRecipes,
                'Нет спецификаций набора / ТП50',
              )}
              {renderRecipeFolder(
                'Полуфабрикаты',
                semiRecipes,
                'Нет спецификации ПФ — создайте через «+» / «+ Полуфабрикат»',
              )}
            </>
          )}
        </section>
      )}

      {/* —— Выработка —— */}
      {subtab === 'produce' && canWrite && (
        <section className="stock-card production-card">
          <div className="stock-card-head">
            <div>
              <h3>Выработка</h3>
              <p className="hint-small">
                Мастер: продукт / лот → preview списания → проведение + PDF
              </p>
            </div>
          </div>

          <div className="produce-wizard-steps" role="tablist" aria-label="Шаги выработки">
            {[
              { n: 1, label: 'Продукт и лот' },
              { n: 2, label: 'Preview' },
              { n: 3, label: 'Готово' },
            ].map((s) => (
              <button
                key={s.n}
                type="button"
                role="tab"
                aria-selected={produceStep === s.n}
                className={`produce-step-chip${produceStep === s.n ? ' active' : ''}${produceStep > s.n ? ' done' : ''}`}
                onClick={() => {
                  if (s.n < produceStep) setProduceStep(s.n);
                }}
                disabled={s.n > produceStep}
              >
                <span className="produce-step-num">{s.n}</span>
                {s.label}
              </button>
            ))}
          </div>

          {produceStep === 1 && (
            <>
              <div className="production-form-section">
                <h4>1. Основные данные</h4>
                <div className="production-form-grid production-form-main">
                  <label className="production-field-product">Продукт
                    <select
                      value={produceForm.product_sku}
                      onChange={(e) => setProduceForm({
                        ...produceForm,
                        product_sku: e.target.value,
                        recipe_id: '',
                        preferred_by_group: {},
                        preferred_lots: {},
                      })}
                    >
                      {finishedProduceSkus.length > 0 && (
                        <optgroup label="Готовая продукция">
                          {finishedProduceSkus.map((p) => (
                            <option key={p.sku} value={p.sku}>{p.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {semiProduceSkus.length > 0 && (
                        <optgroup label="Полуфабрикаты">
                          {semiProduceSkus.map((p) => (
                            <option key={p.sku} value={p.sku}>
                              {p.label}{p.subtitle ? ` · ${p.subtitle}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </label>
                  {recipes.filter((r) => r.product_sku === produceForm.product_sku).length > 1 && (
                    <label className="production-field-variant">Спецификация
                      <select
                        value={produceForm.recipe_id || recipe?.id || ''}
                        onChange={(e) => setProduceForm({
                          ...produceForm, recipe_id: e.target.value, preferred_by_group: {},
                        })}
                      >
                        {recipes.filter((r) => r.product_sku === produceForm.product_sku).map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label>Количество, шт
                    <input
                      type="number"
                      min="1"
                      value={produceForm.qty}
                      onChange={(e) => setProduceForm({ ...produceForm, qty: e.target.value })}
                    />
                  </label>
                  <label>Архив, шт
                    <input
                      type="number"
                      min="0"
                      value={produceForm.archive_qty}
                      onChange={(e) => setProduceForm({ ...produceForm, archive_qty: e.target.value })}
                      title="Полный набор по спецификации — суммируется к списанию"
                    />
                  </label>
                  <label>
                    {recipe?.output_kind === 'raw' ? 'LOT полуфабриката' : 'LOT готовой продукции'}
                    <input
                      value={produceForm.lot_number}
                      onChange={(e) => setProduceForm({ ...produceForm, lot_number: e.target.value })}
                    />
                  </label>
                  <label>Срок годности (ДД.ММ.ГГГГ)
                    <input
                      inputMode="numeric"
                      placeholder="ДД.ММ.ГГГГ"
                      value={produceForm.expiration_date}
                      onChange={(e) => setProduceForm({
                        ...produceForm,
                        expiration_date: formatDmyInput(e.target.value),
                      })}
                    />
                  </label>
                  <label>Окончание производства
                    <input
                      type="date"
                      value={produceForm.production_end_date}
                      onChange={(e) => {
                        const production_end_date = e.target.value;
                        const patch = { production_end_date };
                        if (production_end_date && /^\d{4}-\d{2}-\d{2}$/.test(production_end_date)) {
                          patch.period_from = `${production_end_date.slice(0, 7)}-01`;
                          patch.period_to = production_end_date;
                        }
                        setProduceForm({ ...produceForm, ...patch });
                      }}
                    />
                  </label>
                  <label>Период производства: с
                    <input
                      type="date"
                      value={produceForm.period_from}
                      onChange={(e) => setProduceForm({ ...produceForm, period_from: e.target.value })}
                    />
                  </label>
                  <label>по
                    <input
                      type="date"
                      value={produceForm.period_to}
                      onChange={(e) => setProduceForm({ ...produceForm, period_to: e.target.value })}
                    />
                  </label>
                </div>
              </div>
              {recipe?.output_kind === 'raw' && (
                <div className="bom-output-note production-output-note">
                  После проведения <b>{productLabel(recipe.product_sku, products)}</b> автоматически поступит
                  в папку «Полуфабрикаты» склада сырья
                  {recipe.output_raw_code ? <> (код <b>{recipe.output_raw_code}</b>)</> : null}
                  {' '}и станет доступен в спецификации готовой продукции.
                </div>
              )}

              {(groupOptions.length > 0 || (recipe?.lines || []).some((l) => (
                materials.find((m) => m.code === l.raw_code)?.lots?.length > 1
              ))) && (
                <div className="production-form-section">
                  <h4>Настройки списания (опционально)</h4>
                  <div className="production-form-grid production-form-options">
                    {groupOptions.map(([group, opts]) => (
                      <label key={group}>
                        {groupLabel(group)}: приоритет
                      <select
                        value={produceForm.preferred_by_group[group] || ''}
                        onChange={(e) => setProduceForm({
                          ...produceForm,
                          preferred_by_group: {
                            ...produceForm.preferred_by_group,
                            [group]: e.target.value,
                          },
                        })}
                      >
                        <option value="">По порядку спецификации</option>
                        {opts.map((o) => (
                          <option key={o.raw_code} value={o.raw_code}>
                            {o.raw_name} ({fmtNum(o.raw_qty)})
                          </option>
                        ))}
                      </select>
                      </label>
                    ))}
                    {(recipe?.lines || []).filter((l) => (
                      materials.find((m) => m.code === l.raw_code)?.lots?.length > 1
                    )).slice(0, 6).map((l) => {
                      const mat = materials.find((m) => m.code === l.raw_code);
                      const lots = mat?.lots || [];
                      return (
                        <label key={`lot-${l.raw_code}`}>
                          LOT · {(l.raw_name || '').slice(0, 34)}
                          <select
                            value={produceForm.preferred_lots[l.raw_code] || ''}
                            onChange={(e) => setProduceForm({
                              ...produceForm,
                              preferred_lots: {
                                ...produceForm.preferred_lots,
                                [l.raw_code]: e.target.value,
                              },
                            })}
                          >
                            <option value="">FIFO — первая поступившая</option>
                            {lots.map((lot) => (
                              <option key={lot.id} value={lot.id}>
                                {lot.lot_number || 'без LOT'} ({fmtNum(lot.qty)})
                              </option>
                            ))}
                          </select>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="production-form-section">
                <label className="production-field-comment">Комментарий
                  <input
                    value={produceForm.comment}
                    onChange={(e) => setProduceForm({ ...produceForm, comment: e.target.value })}
                    placeholder="Примечание к выработке"
                  />
                </label>
                {recipe ? (
                  <p className="hint-small" style={{ marginTop: 8 }}>
                    Спецификация: <b>{recipe.name}</b>
                  </p>
                ) : (
                  <p className="hint-small" style={{ marginTop: 8 }}>
                    Спецификации нет — соберите во вкладке «Спецификация».
                  </p>
                )}
                <div className="production-form-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={produceBusy || !recipe}
                    onClick={goProduceStep2}
                  >
                    {produceBusy ? 'Считаем…' : 'Далее · Preview списания'}
                  </button>
                </div>
              </div>
            </>
          )}

          {produceStep === 2 && (
            <div className="production-form-section">
              <h4>2. Preview списания</h4>
              <p className="hint-small">
                Списание = выпуск + архив (полный набор). Брак и лаба — отдельным актом ниже.
              </p>
              {preview ? (
                <div className="prod-preview">
                  <h4>
                    {preview.qty} шт
                    {preview.archive_qty ? ` + архив ${preview.archive_qty}` : ''}
                    {' '}
                    {preview.product_name}
                    {preview.consume_qty ? ` (= ${preview.consume_qty} наборов)` : ''}
                  </h4>
                  <table className="stats-table">
                    <thead>
                      <tr><th>Сырьё</th><th>Группа</th><th>Списать</th><th>Есть</th></tr>
                    </thead>
                    <tbody>
                      {(preview.consumed || []).map((c, idx) => (
                        <tr key={`${c.raw_code}-${c.line_group || ''}-${idx}`}>
                          <td>{c.raw_name || c.raw_code}</td>
                          <td className="cell-muted">{c.line_group ? groupLabel(c.line_group) : '—'}</td>
                          <td><b>{fmtNum(c.need)}</b></td>
                          <td className={Number(c.have) < Number(c.need) ? 'qty-neg' : ''}>
                            {fmtNum(c.have)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="hint-small">Нет preview — вернитесь на шаг 1.</p>
              )}
              <div className="production-form-actions">
                <button type="button" className="btn-secondary" onClick={() => setProduceStep(1)} disabled={produceBusy}>
                  ← Назад
                </button>
                <button type="button" className="btn-header" onClick={runPreview} disabled={produceBusy}>
                  Обновить preview
                </button>
                <button type="button" className="btn-primary" onClick={submitProduce} disabled={produceBusy || !preview}>
                  {produceBusy ? 'Проводим…' : 'Провести выработку + PDF'}
                </button>
              </div>
            </div>
          )}

          {produceStep === 3 && (
            <div className="production-form-section produce-wizard-done">
              <h4>3. Готово</h4>
              <p>
                Выработка проведена
                {lastProductionId ? <> · документ <b>#{lastProductionId}</b></> : null}.
                PDF открыт (или доступен из журнала).
              </p>
              <div className="production-form-actions">
                {lastProductionId && (
                  <button type="button" className="btn-secondary" onClick={() => openReportPdf(lastProductionId)}>
                    Открыть PDF снова
                  </button>
                )}
                <button type="button" className="btn-primary" onClick={resetProduceWizard}>
                  Новая выработка
                </button>
              </div>
            </div>
          )}

          <div className="stock-card-head" style={{ marginTop: 28 }}>
            <div>
              <h3>Брак / лаборатория</h3>
              <p className="hint-small">
                Отдельный акт по рецепту выработки (норма scrap/lab). Выберите выработку — сырьё подтянется из её snapshot, не «первый tp50».
              </p>
            </div>
          </div>
          <div className="production-form-section">
            <div className="production-form-grid production-form-main">
              <label>Продукт
                <select
                  value={scrapLabForm.product_sku}
                  onChange={(e) => {
                    const product_sku = e.target.value;
                    setScrapLabForm({
                      ...scrapLabForm, product_sku, raw_code: '', production_id: '',
                    });
                    setScrapSuggest(null);
                  }}
                >
                  {products.map((p) => (
                    <option key={p.sku} value={p.sku}>{p.label}</option>
                  ))}
                </select>
              </label>
              <label>Выработка (опц.)
                <select
                  value={scrapLabForm.production_id}
                  onChange={(e) => {
                    const production_id = e.target.value;
                    const p = productions.find((x) => String(x.id) === String(production_id));
                    const next = {
                      ...scrapLabForm,
                      production_id,
                      raw_code: '',
                      lot_number: p?.lot_number || (production_id ? '' : scrapLabForm.lot_number),
                      product_sku: p?.product_sku || scrapLabForm.product_sku,
                    };
                    setScrapLabForm(next);
                    setScrapSuggest(null);
                    const qty = (parseInt(next.scrap_qty, 10) || 0) + (parseInt(next.lab_qty, 10) || 0);
                    if (qty > 0 && next.product_sku) {
                      loadScrapSuggest({
                        production_id,
                        raw_code: '',
                        lot_number: next.lot_number,
                        product_sku: next.product_sku,
                      });
                    }
                  }}
                >
                  <option value="">— вручную —</option>
                  {productions
                    .filter((p) => p.product_sku === scrapLabForm.product_sku || !scrapLabForm.product_sku)
                    .slice(0, 30)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        #{p.id} · {p.lot_number || 'без LOT'} · {fmtNum(p.qty)} шт
                      </option>
                    ))}
                </select>
              </label>
              <label>LOT ГП
                <input
                  value={scrapLabForm.lot_number}
                  onChange={(e) => setScrapLabForm({ ...scrapLabForm, lot_number: e.target.value })}
                />
              </label>
              <label>Брак, шт
                <input
                  type="number"
                  min="0"
                  value={scrapLabForm.scrap_qty}
                  onChange={(e) => {
                    const scrap_qty = e.target.value;
                    setScrapLabForm({ ...scrapLabForm, scrap_qty });
                    loadScrapSuggest({ scrap_qty });
                  }}
                />
              </label>
              <label>Лаборатория, шт
                <input
                  type="number"
                  min="0"
                  value={scrapLabForm.lab_qty}
                  onChange={(e) => {
                    const lab_qty = e.target.value;
                    setScrapLabForm({ ...scrapLabForm, lab_qty });
                    loadScrapSuggest({ lab_qty });
                  }}
                />
              </label>
              <label>Дата списания
                <input
                  type="date"
                  value={scrapLabForm.moved_at}
                  onChange={(e) => setScrapLabForm({ ...scrapLabForm, moved_at: e.target.value })}
                />
              </label>
              <label>Сырьё (полоски)
                <select
                  value={scrapLabForm.raw_code}
                  onChange={(e) => setScrapLabForm({ ...scrapLabForm, raw_code: e.target.value })}
                >
                  <option value="">Авто из спецификации</option>
                  {(scrapSuggest?.lines || []).map((l) => (
                    <option key={l.raw_code} value={l.raw_code}>
                      {l.code_1c ? `${l.code_1c} · ` : ''}
                      {l.name} · ×{fmtNorm(l.qty_per_unit)} → {fmtNum(l.qty)} (есть {fmtNum(l.have)})
                    </option>
                  ))}
                  {!scrapLabForm.production_id && !(scrapSuggest?.lines || []).length && materials
                    .filter((m) => /row\s*strips|row\s*of\s*\d+|сыр.*полоск|полоск.*глюкоз|dry8014/i.test(m.name || ''))
                    .map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.code_1c ? `${m.code_1c} · ` : ''}{m.name} ({fmtNum(m.qty)})
                      </option>
                    ))}
                </select>
              </label>
              <label className="production-field-comment">Комментарий
                <input
                  value={scrapLabForm.comment}
                  onChange={(e) => setScrapLabForm({ ...scrapLabForm, comment: e.target.value })}
                />
              </label>
            </div>
            <p className="hint-small">
              Можно только лабу (брак = 0). Брак+лаба → один общий PDF «Акт списания»; в журнале ниже — повторное скачивание.
              {scrapSuggest?.recipe?.name
                ? ` Рецепт: ${scrapSuggest.recipe.name}.`
                : ''}
              {scrapSuggest?.lines?.length
                ? ` Норма: ${scrapSuggest.lines.map((l) => `${l.code_1c || l.raw_code} ×${fmtNorm(l.qty_per_unit)}`).join(' · ')}`
                : ''}
            </p>
            <div className="production-form-actions">
              <button type="button" className="btn-primary" onClick={submitScrapLab}>
                Списать и сформировать акт
              </button>
            </div>
          </div>

          <div className="stock-card-head" style={{ marginTop: 20 }}>
            <h3>Журнал актов брак / лаборатория</h3>
            <p className="hint-small">
              PDF без печати. Двойной PDF — брак+лаба по лоту. Крестик удаляет акт и возвращает сырьё.
            </p>
          </div>
          {(() => {
            const scrapLabActs = writeoffs.filter((w) => w.kind === 'scrap' || w.kind === 'lab');
            if (!scrapLabActs.length) {
              return <p className="hint-small">Пока нет актов брака/лабы.</p>;
            }
            const byLot = new Map();
            for (const w of scrapLabActs) {
              const key = String(w.lot_number || '').trim() || `id:${w.id}`;
              if (!byLot.has(key)) byLot.set(key, []);
              byLot.get(key).push(w);
            }
            return (
              <div className="stock-journal-scroll">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Тип</th>
                      <th>Продукт / LOT</th>
                      <th>Qty</th>
                      <th>Кто</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {scrapLabActs.map((w) => {
                      const lotKey = String(w.lot_number || '').trim();
                      const siblings = lotKey
                        ? (byLot.get(lotKey) || []).filter((x) => x.kind === 'scrap' || x.kind === 'lab')
                        : [w];
                      const canCombine = siblings.length > 1
                        && siblings.some((x) => x.kind === 'scrap')
                        && siblings.some((x) => x.kind === 'lab');
                      return (
                        <tr key={w.id}>
                          <td className="cell-nowrap">{fmtDateTime(w.moved_at || w.created_at)}</td>
                          <td>{w.title || w.kind}</td>
                          <td className="cell-muted">
                            {w.product_sku ? productLabel(w.product_sku, products) : '—'}
                            {w.lot_number ? ` · ${w.lot_number}` : ''}
                          </td>
                          <td className="cell-nowrap">{w.qty > 0 ? `${fmtNum(w.qty)} шт` : '—'}</td>
                          <td className="cell-muted">{w.created_by || '—'}</td>
                          <td className="cell-nowrap">
                            <div className="table-actions">
                              <button
                                type="button"
                                className="icon-btn"
                                title="PDF"
                                aria-label="PDF"
                                onClick={() => openWriteoffPdf(w.id)}
                              >
                                <UiIcon name="pdf" />
                              </button>
                              {canCombine && w.kind === 'scrap' && (
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="PDF оба"
                                  aria-label="PDF оба"
                                  onClick={() => openCombinedWriteoffPdf(
                                    siblings.map((x) => x.id),
                                    w.lot_number,
                                  )}
                                >
                                  <UiIcon name="pdfBoth" />
                                </button>
                              )}
                              {canWrite && (
                                <button
                                  type="button"
                                  className="icon-btn icon-btn-danger"
                                  title="Удалить"
                                  aria-label="Удалить"
                                  onClick={() => deleteWriteoffAct(w)}
                                >
                                  <UiIcon name="close" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          <div className="stock-card-head" style={{ marginTop: 20 }}>
            <div>
              <h3>Журнал выработок</h3>
              <p className="hint-small">PDF, правка реквизитов и удаление дубля в строке. Сырьё — раскрывается отдельно.</p>
            </div>
            <div className="chip-filter" role="group" aria-label="Фильтр выработок">
              {[
                { id: 'all', label: 'Все' },
                { id: 'pf', label: 'ПФ' },
                { id: 'tp50', label: 'ТП50' },
                { id: 'gluck', label: 'Глюкометры' },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={prodJournalFilter === f.id ? 'active' : ''}
                  onClick={() => setProdJournalFilter(f.id)}
                >
                  {f.label}
                  <span className="chip-count">{prodJournalCounts[f.id] || 0}</span>
                </button>
              ))}
            </div>
          </div>
          {productions.length === 0 ? (
            <p className="hint-small">Пока нет.</p>
          ) : filteredProductions.length === 0 ? (
            <p className="hint-small">Нет выработок в этом фильтре.</p>
          ) : (
            <div className="prod-journal">
              {filteredProductions.map((p) => {
                const mats = p.materials || [];
                const editing = editProd?.id === p.id;
                const periodLabel = (p.period_from || p.period_to)
                  ? `${formatIsoRu(p.period_from) || '…'} — ${formatIsoRu(p.period_to) || '…'}`
                  : '—';
                const datesCompact = [
                  periodLabel !== '—' ? periodLabel : (formatIsoRu(p.production_end_date) || null),
                  formatIsoRu(p.expiration_date) ? `годн. ${formatIsoRu(p.expiration_date)}` : null,
                ].filter(Boolean).join(' · ') || '—';
                const archive = Number(p.archive_qty) || 0;
                return (
                  <article
                    key={p.id}
                    className={`prod-journal-card${editing ? ' is-editing' : ''}`}
                  >
                    <div className="prod-journal-tab">
                      <div className="prod-journal-summary">
                        <span className="prod-journal-num">#{p.id}</span>
                        <span className="prod-journal-product">{productLabel(p.product_sku, products)}</span>
                        <span className="prod-journal-lot">{p.lot_number || 'без LOT'}</span>
                        <span className="prod-journal-qty">
                          {fmtNum(p.qty)} шт
                          {archive > 0 ? ` · арх. ${fmtNum(archive)}` : ''}
                        </span>
                        <span className="prod-journal-dates">{datesCompact}</span>
                        <span className="prod-journal-when cell-muted" title={p.comment || p.created_by || ''}>
                          {fmtDateTime(p.created_at)}
                          {p.created_by ? ` · ${p.created_by}` : ''}
                        </span>
                      </div>
                      <div className="prod-journal-summary-actions">
                        <button
                          type="button"
                          className="icon-btn"
                          title="PDF"
                          aria-label="PDF"
                          onClick={() => openReportPdf(p.id)}
                        >
                          <UiIcon name="pdf" />
                        </button>
                        {canWrite && !editing && (
                          <>
                            <button
                              type="button"
                              className="icon-btn"
                              title="Изменить"
                              aria-label="Изменить"
                              onClick={() => startEditProduction(p)}
                            >
                              <UiIcon name="gear" />
                            </button>
                            <button
                              type="button"
                              className="icon-btn icon-btn-danger"
                              title="Удалить дубль"
                              aria-label="Удалить"
                              onClick={() => deleteProductionRun(p)}
                            >
                              <UiIcon name="close" />
                            </button>
                          </>
                        )}
                        {editing && (
                          <button
                            type="button"
                            className="icon-btn"
                            title="Закрыть"
                            aria-label="Закрыть"
                            onClick={() => setEditProd(null)}
                          >
                            <UiIcon name="close" />
                          </button>
                        )}
                      </div>
                    </div>

                    {editing && (
                          <div className="prod-journal-edit" ref={editProdPanelRef}>
                            <div className="prod-journal-edit-head">
                              <strong>Реквизиты отчёта</strong>
                              <span>
                                увеличение «упаковано» доспишет сырьё по спецификации.
                                {editProd.isPf
                                  ? ' ПФ по умолчанию не приходуем — если +Δ уже стоит приходом / на другой строке лота.'
                                  : ' архив = полный набор в расходе; недобор доспишется при сохранении.'}
                              </span>
                            </div>
                            <div className="production-form-grid production-form-main prod-journal-edit-grid">
                              <label>LOT готовой продукции
                                <input
                                  value={editProd.lot_number}
                                  onChange={(e) => setEditProd({ ...editProd, lot_number: e.target.value })}
                                />
                              </label>
                              <label>Упаковано, шт
                                <input
                                  type="number"
                                  min={canAdmin ? 1 : editProd.origQty}
                                  step="1"
                                  disabled={!canAdmin}
                                  value={editProd.qty}
                                  onChange={(e) => setEditProd({ ...editProd, qty: e.target.value })}
                                />
                              </label>
                              {editProd.isPf && canAdmin && (
                                <label className="production-field-comment">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(editProd.skip_output)}
                                    onChange={(e) => setEditProd({
                                      ...editProd,
                                      skip_output: e.target.checked,
                                    })}
                                  />
                                  {' '}ПФ уже на складе — не приходовать +Δ
                                </label>
                              )}
                              <label>Архив, шт
                                <input
                                  type="number"
                                  min="0"
                                  value={editProd.archive_qty}
                                  onChange={(e) => setEditProd({ ...editProd, archive_qty: e.target.value })}
                                />
                              </label>
                              <label>Срок годности (ДД.ММ.ГГГГ)
                                <input
                                  inputMode="numeric"
                                  placeholder="ДД.ММ.ГГГГ"
                                  value={editProd.expiration_date}
                                  onChange={(e) => setEditProd({
                                    ...editProd,
                                    expiration_date: formatDmyInput(e.target.value),
                                  })}
                                />
                              </label>
                              <label>Окончание производства
                                <input
                                  type="date"
                                  value={editProd.production_end_date}
                                  onChange={(e) => {
                                    const production_end_date = e.target.value;
                                    const patch = { production_end_date };
                                    if (production_end_date && /^\d{4}-\d{2}-\d{2}$/.test(production_end_date)) {
                                      patch.period_from = `${production_end_date.slice(0, 7)}-01`;
                                      patch.period_to = production_end_date;
                                    }
                                    setEditProd({ ...editProd, ...patch });
                                  }}
                                />
                              </label>
                              <label>Период: с
                                <input
                                  type="date"
                                  value={editProd.period_from}
                                  onChange={(e) => setEditProd({ ...editProd, period_from: e.target.value })}
                                />
                              </label>
                              <label>по
                                <input
                                  type="date"
                                  value={editProd.period_to}
                                  onChange={(e) => setEditProd({ ...editProd, period_to: e.target.value })}
                                />
                              </label>
                              <label className="production-field-comment">Комментарий
                                <input
                                  value={editProd.comment}
                                  onChange={(e) => setEditProd({ ...editProd, comment: e.target.value })}
                                />
                              </label>
                            </div>
                            <div className="production-form-actions">
                              <button
                                type="button"
                                className="btn-primary"
                                disabled={editProdSaving}
                                onClick={saveEditProduction}
                              >
                                {editProdSaving ? 'Сохраняю…' : 'Сохранить'}
                              </button>
                              <button
                                type="button"
                                className="btn-header"
                                disabled={editProdSaving}
                                onClick={() => setEditProd(null)}
                              >
                                Отмена
                              </button>
                            </div>
                          </div>
                        )}

                        <details className="prod-journal-mats">
                          <summary>
                            Движения сырья
                            <span className="cell-muted"> · {mats.length || 0} поз.</span>
                          </summary>
                          {mats.length > 0 ? (
                            <div className="production-material-list prod-journal-mat-list">
                              {mats.map((x) => {
                                const net = Number(x.qty) || 0;
                                const zeroed = net === 0 && x.adjusted;
                                return (
                                  <div className="production-material-row" key={x.raw_code}>
                                    <span>
                                      <small>
                                        {zeroed ? 'Сторнировано' : (net < 0 ? 'Списано' : 'Получено')}
                                      </small>
                                      {x.name}
                                    </span>
                                    <b className={net < 0 ? 'qty-neg' : (net > 0 ? 'qty-pos' : 'cell-muted')}>
                                      {net > 0 ? '+' : ''}
                                      {fmtNum(net)} {x.unit || ''}
                                    </b>
                                    {canAdmin && x.has_expense && !x.adjusted && (
                                      <button
                                        type="button"
                                        className="btn-link"
                                        onClick={() => revokeProductionLine(p, x)}
                                      >
                                        Правка
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="hint-small">Нет движений.</p>
                          )}
                        </details>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {subtab === 'produce' && !canWrite && (
        <p className="hint-small">Просмотр: выработка недоступна для вашей роли.</p>
      )}

      {/* —— Расходники / акты —— */}
      {subtab === 'writeoffs' && canWrite && (
        <section className="stock-card production-card">
          <div className="stock-card-head">
            <div>
              <h3>Списание расходных материалов</h3>
              <p className="hint-small">
                Коробки, скотч, канцтовары и пр. — без привязки к лоту ГП. Каждый акт = PDF.
              </p>
            </div>
          </div>
          <div className="production-form-section">
            <div className="production-form-grid production-form-main">
              <label>Дата списания
                <input
                  type="date"
                  value={suppliesForm.moved_at}
                  onChange={(e) => setSuppliesForm({ ...suppliesForm, moved_at: e.target.value })}
                />
              </label>
              <label className="production-field-comment">Комментарий
                <input
                  value={suppliesForm.comment}
                  onChange={(e) => setSuppliesForm({ ...suppliesForm, comment: e.target.value })}
                  placeholder="Напр. хознужды склада"
                />
              </label>
            </div>
            {(suppliesForm.lines || []).map((line, idx) => (
              <div className="production-form-grid production-form-main" key={`sup-${idx}`}>
                <label className="grow">Позиция
                  <MaterialPicker
                    materials={materials}
                    value={line.raw_code}
                    onChange={(code) => {
                      const lines = [...suppliesForm.lines];
                      lines[idx] = { ...lines[idx], raw_code: code };
                      setSuppliesForm({ ...suppliesForm, lines });
                    }}
                    fetchMaterials={fetchMaterials}
                  />
                </label>
                <label>Кол-во
                  <input
                    type="number"
                    min="0.0001"
                    step="any"
                    value={line.qty}
                    onChange={(e) => {
                      const lines = [...suppliesForm.lines];
                      lines[idx] = { ...lines[idx], qty: e.target.value };
                      setSuppliesForm({ ...suppliesForm, lines });
                    }}
                  />
                </label>
                <label>&nbsp;
                  <button
                    type="button"
                    className="btn-header"
                    disabled={suppliesForm.lines.length <= 1}
                    onClick={() => {
                      const lines = suppliesForm.lines.filter((_, i) => i !== idx);
                      setSuppliesForm({ ...suppliesForm, lines });
                    }}
                  >
                    Удалить
                  </button>
                </label>
              </div>
            ))}
            <div className="production-form-actions">
              <button
                type="button"
                className="btn-header"
                onClick={() => setSuppliesForm({
                  ...suppliesForm,
                  lines: [...suppliesForm.lines, { raw_code: '', qty: '' }],
                })}
              >
                + строка
              </button>
              <button type="button" className="btn-primary" onClick={submitSupplies}>
                Списать и сформировать акт
              </button>
            </div>
          </div>

          <div className="stock-card-head" style={{ marginTop: 20 }}>
            <h3>Журнал актов списания</h3>
            <p className="hint-small">
              PDF без автопечати. Крестик — сторно сырья на те же партии. Брак+лаба: двойной PDF на вкладке Выработка.
            </p>
          </div>
          {writeoffs.length === 0 ? (
            <p className="hint-small">Пока нет.</p>
          ) : (
            <div className="stock-journal-scroll">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Дата</th><th>Тип</th><th>Продукт / LOT</th>
                    <th>Qty</th><th>Строк</th><th>Кто</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {writeoffs.map((w) => {
                    const lotKey = String(w.lot_number || '').trim();
                    const siblings = (w.kind === 'scrap' || w.kind === 'lab') && lotKey
                      ? writeoffs.filter((x) => (
                        (x.kind === 'scrap' || x.kind === 'lab')
                        && String(x.lot_number || '').trim() === lotKey
                      ))
                      : [w];
                    const canCombine = siblings.length > 1
                      && siblings.some((x) => x.kind === 'scrap')
                      && siblings.some((x) => x.kind === 'lab');
                    return (
                      <tr key={w.id}>
                        <td className="cell-nowrap">{fmtDateTime(w.moved_at || w.created_at)}</td>
                        <td>{w.title || w.kind}</td>
                        <td className="cell-muted">
                          {w.product_sku ? productLabel(w.product_sku, products) : '—'}
                          {w.lot_number ? ` · ${w.lot_number}` : ''}
                        </td>
                        <td>{w.qty > 0 ? fmtNum(w.qty) : fmtNum(w.total_qty)}</td>
                        <td>{w.lines_count || '—'}</td>
                        <td className="cell-muted">{w.created_by || '—'}</td>
                        <td className="cell-nowrap">
                          <div className="table-actions">
                            <button
                              type="button"
                              className="icon-btn"
                              title="PDF"
                              aria-label="PDF"
                              onClick={() => openWriteoffPdf(w.id)}
                            >
                              <UiIcon name="pdf" />
                            </button>
                            {canCombine && w.kind === 'scrap' && (
                              <button
                                type="button"
                                className="icon-btn"
                                title="PDF оба"
                                aria-label="PDF оба"
                                onClick={() => openCombinedWriteoffPdf(
                                  siblings.map((x) => x.id),
                                  w.lot_number,
                                )}
                              >
                                <UiIcon name="pdfBoth" />
                              </button>
                            )}
                            {canWrite && (
                              <button
                                type="button"
                                className="icon-btn icon-btn-danger"
                                title="Удалить"
                                aria-label="Удалить"
                                onClick={() => deleteWriteoffAct(w)}
                              >
                                <UiIcon name="close" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {subtab === 'writeoffs' && !canWrite && (
        <p className="hint-small">Просмотр: списание недоступно для вашей роли.</p>
      )}

      {/* —— Журналы —— */}
      {subtab === 'journals' && (
        <>
          <section className="stock-card">
            <div className="stock-card-head"><h3>Движения сырья</h3></div>
            {movements.length === 0 ? (
              <p className="hint-small">Пусто.</p>
            ) : (
              <div className="stock-journal-scroll">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Дата</th><th>Тип</th><th>Сырьё</th><th>LOT</th><th>Qty</th><th>Коммент</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((m) => (
                      <tr key={m.id}>
                        <td className="cell-nowrap">
                          {m.moved_at ? formatIsoRu(m.moved_at) : fmtDateTime(m.created_at)}
                        </td>
                        <td>{m.type}</td>
                        <td>{m.raw_name}</td>
                        <td className="cell-muted">{m.lot_number || '—'}</td>
                        <td className={Number(m.qty) < 0 ? 'qty-neg' : 'qty-pos'}>
                          {fmtNum(m.qty)}
                        </td>
                        <td className="cell-muted">{m.comment || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="stock-card">
            <div className="stock-card-head"><h3>Журнал правок</h3></div>
            {audit.length === 0 ? (
              <p className="hint-small">Правок ещё нет.</p>
            ) : (
              <div className="stock-journal-scroll">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Когда</th><th>Кто</th><th>Объект</th><th>Действие</th><th>Комментарий</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((row) => (
                      <tr key={row.id}>
                        <td className="cell-nowrap">{fmtDateTime(row.created_at)}</td>
                        <td className="cell-muted">{row.created_by || '—'}</td>
                        <td>{row.entity_type}:{row.entity_id}</td>
                        <td>{row.action}</td>
                        <td>{row.comment}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {materialHistory && (
        <div className="modal-overlay" onClick={() => setMaterialHistory(null)}>
          <div className="modal-card raw-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="raw-history-head">
              <div>
                <span className="raw-history-code">
                  {materialHistory.material?.code_1c || materialHistory.material?.code}
                </span>
                <h3>{materialHistory.material?.name || 'История позиции'}</h3>
              </div>
              <div className="raw-history-actions">
                {materialHistory.material?.code && (
                  <button
                    type="button"
                    className="btn-secondary btn-compact"
                    disabled={historyLoading}
                    onClick={() => openProductionSummaryPdf(materialHistory.material.code)}
                  >
                    PDF сводка
                  </button>
                )}
                {canWrite && materialHistory.material?.name && (
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => {
                      openEditMaterial(materialHistory.material);
                      setMaterialHistory(null);
                    }}
                    aria-label="Изменить позицию"
                    title="Изменить позицию"
                  >
                    <UiIcon name="gear" />
                  </button>
                )}
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setMaterialHistory(null)}
                  aria-label="Закрыть"
                  title="Закрыть"
                >
                  <UiIcon name="close" />
                </button>
              </div>
            </div>

            {historyLoading ? (
              <p className="hint-small">Загружаю движения…</p>
            ) : (
              <div className="raw-history-body">
                <div className="raw-history-summary">
                  <div>
                    <span>Текущий остаток</span>
                    <b>{fmtNum(materialHistory.material?.qty)} {materialHistory.material?.unit_expense || materialHistory.material?.unit}</b>
                  </div>
                  <div>
                    <span>Всего поступило</span>
                    <b className="qty-pos">+{fmtNum(materialHistory.totals?.total_in)}</b>
                  </div>
                  <div>
                    <span>Всего списано</span>
                    <b className="qty-neg">−{fmtNum(materialHistory.totals?.total_out)}</b>
                  </div>
                  <div>
                    <span>Операций</span>
                    <b>{fmtNum(materialHistory.totals?.movement_count)}</b>
                  </div>
                </div>

                {(materialHistory.lots || []).some((lot) => Number(lot.qty) > 0) && (
                  <div className="raw-history-lots">
                    <span>Остатки по LOT</span>
                    <div>
                      {(materialHistory.lots || []).filter((lot) => Number(lot.qty) > 0).map((lot) => (
                        <span className="raw-history-lot" key={lot.id}>
                          {lot.lot_number || 'без LOT'} · <b>{fmtNum(lot.qty)}</b>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="raw-history-tabs">
                  <button
                    type="button"
                    className={historyTab === 'journal' ? 'active' : ''}
                    onClick={() => setHistoryTab('journal')}
                  >
                    Журнал
                    <span className="raw-history-tab-count">
                      {fmtNum(materialHistory.totals?.movement_count)}
                    </span>
                  </button>
                  {materialHistory.production_summary && (
                    <button
                      type="button"
                      className={historyTab === 'summary' ? 'active' : ''}
                      onClick={() => setHistoryTab('summary')}
                    >
                      Сводка
                    </button>
                  )}
                  <button
                    type="button"
                    className={historyTab === 'replace' ? 'active' : ''}
                    onClick={() => setHistoryTab('replace')}
                  >
                    Замены
                    {materialReplaceGroups.length > 0 && (
                      <span className="raw-history-tab-count">{materialReplaceGroups.length}</span>
                    )}
                  </button>
                </div>

                {historyTab === 'journal' && (
                  <div className="raw-history-panel raw-history-panel-journal">
                    <div className="raw-history-table-wrap">
                      <table className="stats-table raw-history-table">
                        <thead>
                          <tr>
                            <th>Дата</th><th>Операция</th><th>LOT</th>
                            <th>Изменение</th><th>Остаток</th><th>Основание</th><th>Кто</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(materialHistory.movements || []).map((m) => (
                            <tr key={m.id}>
                              <td className="cell-nowrap">
                                {m.moved_at ? formatIsoRu(m.moved_at) : fmtDateTime(m.created_at)}
                              </td>
                              <td>
                                <span className={`raw-movement-type raw-movement-${m.type}`}>
                                  {movementLabel(m.type, m.qty)}
                                </span>
                              </td>
                              <td className="cell-muted">{m.lot_number || '—'}</td>
                              <td className={Number(m.qty) < 0 ? 'qty-neg' : 'qty-pos'}>
                                <b>{Number(m.qty) > 0 ? '+' : ''}{fmtNum(m.qty)}</b>
                              </td>
                              <td><b>{fmtNum(m.balance_after)}</b></td>
                              <td>
                                <div className="raw-history-reason">
                                  <span>{m.comment || '—'}</span>
                                  {m.production_id && (
                                    <button
                                      type="button"
                                      className="btn-link"
                                      onClick={() => openReportPdf(m.production_id)}
                                    >
                                      Выработка #{m.production_id} · PDF
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="cell-muted">{m.created_by || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(materialHistory.movements || []).length === 0 && (
                        <p className="hint-small">Движений по позиции ещё нет.</p>
                      )}
                    </div>
                  </div>
                )}

                {historyTab === 'summary' && materialHistory.production_summary && (
                  <div className="raw-history-panel raw-history-panel-summary">
                    <details className="raw-history-acc" open>
                      <summary>
                        <span>Итоги расхода</span>
                        {materialHistory.production_summary.totals?.check_ok && (
                          <span className="raw-history-prod-ok">Сходится</span>
                        )}
                      </summary>
                      <table className="stats-table raw-history-summary-table">
                        <thead>
                          <tr>
                            <th>Показатель</th>
                            <th>{materialHistory.production_summary.material?.unit || 'шт'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{materialHistory.production_summary.receipt?.label || 'Приход'}</td>
                            <td><b>{fmtNum(materialHistory.production_summary.totals?.receipt)}</b></td>
                          </tr>
                          <tr>
                            <td>Упаковано (выработки)</td>
                            <td>{fmtNum(materialHistory.production_summary.totals?.packed)}</td>
                          </tr>
                          <tr>
                            <td>В архив</td>
                            <td>{fmtNum(materialHistory.production_summary.totals?.archive)}</td>
                          </tr>
                          <tr>
                            <td>Брак (акты)</td>
                            <td>{fmtNum(materialHistory.production_summary.totals?.scrap)}</td>
                          </tr>
                          <tr>
                            <td>Лаборатория (акты)</td>
                            <td>{fmtNum(materialHistory.production_summary.totals?.lab)}</td>
                          </tr>
                          <tr className="raw-history-prod-balance">
                            <td>Остаток / можно на лабу·брак</td>
                            <td><b>{fmtNum(materialHistory.production_summary.totals?.balance)}</b></td>
                          </tr>
                        </tbody>
                      </table>
                    </details>

                    {(materialHistory.production_summary.by_lot || []).length > 0 && (
                      <details className="raw-history-acc">
                        <summary>
                          <span>По лотам готовой продукции</span>
                          <span className="raw-history-tab-count">
                            {materialHistory.production_summary.by_lot.length}
                          </span>
                        </summary>
                        <div className="raw-history-table-wrap raw-history-acc-table">
                          <table className="stats-table raw-history-table">
                            <thead>
                              <tr>
                                <th>LOT</th><th>SKU</th><th>Упаковано</th><th>Архив</th>
                                <th>Брак</th><th>Лаба</th><th>Списано</th><th>Выраб.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {materialHistory.production_summary.by_lot.map((row) => (
                                <tr key={`${row.lot_number}-${row.product_sku}`}>
                                  <td><b>{row.lot_number || '—'}</b></td>
                                  <td>{row.product_sku || '—'}</td>
                                  <td>{fmtNum(row.packed)}</td>
                                  <td>{fmtNum(row.archive)}</td>
                                  <td>{fmtNum(row.scrap)}</td>
                                  <td>{fmtNum(row.lab)}</td>
                                  <td><b>{fmtNum(row.consumed)}</b></td>
                                  <td className="cell-muted">
                                    {(row.production_ids || []).map((id) => `#${id}`).join(', ')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}

                    {(materialHistory.production_summary.productions || []).length > 0 && (
                      <details className="raw-history-acc">
                        <summary>
                          <span>Детализация выработок</span>
                          <span className="raw-history-tab-count">
                            {materialHistory.production_summary.productions.length}
                          </span>
                        </summary>
                        <div className="raw-history-table-wrap raw-history-acc-table">
                          <table className="stats-table raw-history-table">
                            <thead>
                              <tr>
                                <th>#</th><th>LOT</th><th>SKU</th><th>Конец пр-ва</th>
                                <th>Упаковано</th><th>Архив</th><th>Списано сырья</th>
                              </tr>
                            </thead>
                            <tbody>
                              {materialHistory.production_summary.productions.map((p) => (
                                <tr key={p.id}>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn-link"
                                      onClick={() => openReportPdf(p.id)}
                                    >
                                      #{p.id}
                                    </button>
                                  </td>
                                  <td>{p.lot_number || '—'}</td>
                                  <td>{p.product_sku || '—'}</td>
                                  <td className="cell-nowrap">
                                    {p.production_end_date ? formatIsoRu(p.production_end_date) : '—'}
                                  </td>
                                  <td>{fmtNum(p.packed)}</td>
                                  <td>{fmtNum(p.archive)}</td>
                                  <td>{fmtNum(p.raw_expense)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}

                    {(materialHistory.production_summary.anomalies || []).length > 0 && (
                      <p className="hint-small raw-history-prod-note">
                        Корректировки (±сторно) не входят в сводку — нетто 0.
                      </p>
                    )}
                  </div>
                )}

                {historyTab === 'replace' && (
                  <div className="raw-history-panel raw-history-panel-replace">
                    <div className="raw-history-replace raw-history-replace-tab">
                      <span>Замены в спецификациях</span>
                      {materialReplaceGroups.length === 0 ? (
                        <p className="hint-small">
                          Позиция не входит ни в одну спецификацию. Сначала добавьте её
                          в «Спецификация», затем сюда можно линкануть замены —
                          расход пойдёт водопадом по остатку.
                        </p>
                      ) : materialReplaceGroups.map((g) => (
                        <div className="raw-history-replace-group" key={`${g.recipeId}-${g.group || 'solo'}`}>
                          <div className="raw-history-replace-head">
                            <b>{g.group ? groupLabel(g.group) : 'Без группы (обязат.)'}</b>
                            <span className="cell-muted">
                              {g.recipeName} · норма {fmtNorm(g.qty_per_unit)}
                              {g.solo ? ' · линк создаст группу' : ''}
                            </span>
                          </div>
                          <ul className="raw-history-replace-list">
                            {g.siblings.map((s) => {
                              const mat = materials.find((m) => m.code === s.raw_code);
                              const qty = Number(mat?.qty) || 0;
                              const isSelf = s.raw_code === materialHistory.material?.code;
                              return (
                                <li key={`${s.raw_code}-${s.line_group || ''}`} className={isSelf ? 'is-self' : ''}>
                                  <button
                                    type="button"
                                    className="btn-link"
                                    disabled={isSelf}
                                    onClick={() => openMaterialHistory(s.raw_code)}
                                  >
                                    {s.raw_name || mat?.name || s.raw_code}
                                  </button>
                                  <span className={qty > 0 ? '' : 'cell-muted'}>
                                    {fmtNum(qty)} шт
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                          {canAdmin && (
                            <div className="stock-inline-form raw-history-replace-add">
                              <label className="grow">Добавить замену
                                <MaterialPicker
                                  materials={materials.filter(
                                    (m) => !g.siblings.some((s) => s.raw_code === m.code),
                                  )}
                                  value={replaceAdd.recipeId === g.recipeId
                                    && replaceAdd.group === (g.group || '')
                                    ? replaceAdd.raw_code
                                    : ''}
                                  onChange={(code) => setReplaceAdd({
                                    raw_code: code,
                                    recipeId: g.recipeId,
                                    group: g.group || '',
                                  })}
                                  placeholder="Новая позиция в группу"
                                  fetchMaterials={fetchMaterials}
                                />
                              </label>
                              <button
                                type="button"
                                className="btn-primary"
                                disabled={replaceSaving
                                  || !(replaceAdd.recipeId === g.recipeId
                                    && replaceAdd.group === (g.group || '')
                                    && replaceAdd.raw_code)}
                                onClick={() => addMaterialReplacement(g)}
                              >
                                {replaceSaving ? '…' : 'Линкануть'}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {editMaterial && (
        <div className="modal-overlay" onClick={() => setEditMaterial(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Правка {editMaterial.code_1c || editMaterial.code}</h3>
            <label>Наименование
              <input value={editMatForm.name}
                onChange={(e) => setEditMatForm({ ...editMatForm, name: e.target.value })} />
            </label>
            <label>Код 1С
              <input
                value={editMatForm.code_1c || ''}
                onChange={(e) => setEditMatForm({ ...editMatForm, code_1c: e.target.value })}
                placeholder="00-00000165"
              />
            </label>
            <label>Зона склада
              <select
                value={editMatForm.storage_zone || 'other'}
                onChange={(e) => setEditMatForm({ ...editMatForm, storage_zone: e.target.value })}
              >
                {rawZones.map((z) => (
                  <option key={z.code} value={z.code}>{z.walk}. {z.label}</option>
                ))}
              </select>
            </label>
            <label>Ячейка / полка
              <input
                value={editMatForm.storage_bin || ''}
                onChange={(e) => setEditMatForm({ ...editMatForm, storage_bin: e.target.value })}
                placeholder="напр. A-12"
              />
            </label>
            {editMaterial.lot_id != null && (
              <label>LOT
                <input
                  value={editMatForm.lot_number}
                  onChange={(e) => setEditMatForm({ ...editMatForm, lot_number: e.target.value })}
                  placeholder="номер LOT"
                />
              </label>
            )}
            <label>Ед прихода
              <input value={editMatForm.unit_stock}
                onChange={(e) => setEditMatForm({ ...editMatForm, unit_stock: e.target.value, unit: e.target.value })} />
            </label>
            <label>Ед списания
              <input value={editMatForm.unit_expense}
                onChange={(e) => setEditMatForm({ ...editMatForm, unit_expense: e.target.value })} />
            </label>
            <label>Factor (pack→шт)
              <input type="number" step="any" value={editMatForm.factor}
                onChange={(e) => setEditMatForm({ ...editMatForm, factor: e.target.value })} />
            </label>
            <label>Expense factor
              <input type="number" step="any" value={editMatForm.expense_factor}
                onChange={(e) => setEditMatForm({ ...editMatForm, expense_factor: e.target.value })} />
            </label>
            <label>Остаток (base)
              <input type="number" step="any" value={editMatForm.qty}
                onChange={(e) => setEditMatForm({ ...editMatForm, qty: e.target.value })} />
            </label>
            <label>Комментарий *
              <input value={editMatForm.comment}
                onChange={(e) => setEditMatForm({ ...editMatForm, comment: e.target.value })} />
            </label>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={saveMaterial}>Сохранить</button>
              <button type="button" className="btn-header" onClick={() => setEditMaterial(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {editBomLine && (
        <div className="modal-overlay" onClick={() => setEditBomLine(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Спецификация · {editBomLine.raw_name || editBomLine.raw_code}</h3>
            <label>На 1 шт
              <input type="number" step="any" min="0.0001" value={editBomForm.qty_per_unit}
                onChange={(e) => setEditBomForm({ ...editBomForm, qty_per_unit: e.target.value })} />
            </label>
            <label>На 1 брак/лабу
              <input
                type="number"
                step="any"
                min="0"
                value={editBomForm.scrap_lab_qty}
                onChange={(e) => setEditBomForm({ ...editBomForm, scrap_lab_qty: e.target.value })}
                placeholder="пусто = не списывать в акте"
              />
            </label>
            <label>Группа
              <input value={editBomForm.line_group}
                onChange={(e) => setEditBomForm({ ...editBomForm, line_group: e.target.value })} />
            </label>
            <label>Комментарий *
              <input value={editBomForm.comment}
                onChange={(e) => setEditBomForm({ ...editBomForm, comment: e.target.value })} />
            </label>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={saveBomLine}>Сохранить</button>
              <button type="button" className="btn-header" onClick={() => setEditBomLine(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
